(function () {
  'use strict';

  // ─── Utility Functions ───
  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function safeColorHex(hex) {
    return /^#[0-9a-fA-F]{3,8}$/.test(hex) ? hex : '#cccccc';
  }

  function isSilverEngravingColor(color) {
    return String(color || '').toLowerCase() === '#c0c0c0';
  }

  function isBlindEngravingColor(color) {
    return String(color || '').toLowerCase() === 'blind';
  }

  function getDefaultMedia(product) {
    if (product.variants && product.variants.length) {
      for (const v of product.variants) {
        if (v.featuredImage) return v.featuredImage;
      }
    }
    return product.image || '';
  }

  function t(key, fallback) {
    try {
      if (window.AppTranslations && window.AppTranslations[key] !== undefined) {
        return window.AppTranslations[key];
      }
      const parts = key.split('.');
      let node = window.Shopify && window.Shopify.localeData ? window.Shopify.localeData : {};
      for (const p of parts) { node = node && node[p] !== undefined ? node[p] : undefined; }
      if (typeof node === 'string') return node;
    } catch (_) { }
    return fallback;
  }

  class BundleConfigurator extends HTMLElement {
    constructor() {
      super();
      this.productsDataEl = document.querySelector('#bundle-products-data');
      this.products = [];
      this._interiorSort = 'title-ascending';
      this._accessorySort = 'title-ascending';

      // Image cache to keep the canvas fast without network requests
      this._cachedEngravingImage = null;
      this._cachedEngravingSrc = null;
      this._engravingRenderId = 0;
      this._activeGallery = null;
      this._coverThumbnailList = null;
      this._galleryDrag = {
        isMouseDown: false,
        isDragging: false,
        scroller: null,
        startY: 0,
        startScrollTop: 0,
        lastY: 0,
        lastTime: 0,
        velocityY: 0,
        rafId: 0,
        suppressClickUntil: 0,
        touchStart: null
      };

      this._mainSwipe = {
        startX: 0,
        startTime: 0,
        bound: false
      };

      this._carouselMouseDrag = {
        bound: false
      };

      this._savedGallery = null;
      this._modalLocalQty = 1;
      this._colorKeyNavBound = false;

      this._handleGalleryClick = this._handleGalleryClick.bind(this);
      this._handleGalleryMouseDown = this._handleGalleryMouseDown.bind(this);
      this._handleGalleryMouseMove = this._handleGalleryMouseMove.bind(this);
      this._handleGalleryMouseUp = this._handleGalleryMouseUp.bind(this);
      this._handleGalleryTouchStart = this._handleGalleryTouchStart.bind(this);
      this._handleGalleryTouchMove = this._handleGalleryTouchMove.bind(this);
      this._handleGalleryTouchEnd = this._handleGalleryTouchEnd.bind(this);

      this._boundCarouselMouseMove = this._handleCarouselMouseMove.bind(this);
      this._boundCarouselMouseUp = this._handleCarouselMouseUp.bind(this);

      this.state = {
        currentStep: 1,
        coverType: 'original',
        selectedColor: null,
        selectedCover: null,
        ring: 'silver',
        engraving: '',
        engravingColor: '#D4AF37',
        engravingFont: 'Sans Serif',
        engravingFontFamily: "'DM Sans', sans-serif",
        engravingFontWeight: 500,

        interiors: [],
        accessories: [],
        quantities: {},
        interiorPriceMin: null,
        interiorPriceMax: null,
        interiorInStockOnly: false,
        interiorSelectedTags: [],
        accessoryPriceMin: null,
        accessoryPriceMax: null,
        accessoryInStockOnly: false,
        accessorySelectedTags: []
      };

      this._cachedFormatter = null;
      this._currencyFormatter = this._getCurrencyFormatter();
      this.init();
    }

    init() {
      if (this.productsDataEl) {
        try {
          const raw = JSON.parse(this.productsDataEl.textContent);
          this.products = (raw || []).map(p => ({
            ...p,
            id: p.id != null ? p.id : p.productId,
            variantId: p.variantId != null ? p.variantId : (p.variants && p.variants[0] && p.variants[0].id) || null
          }));
        } catch (e) {
          console.error('Failed to parse products data:', e);
        }
      }
      this._updatePriceFilterRange();
      this.bindEvents();
      this.bindModalEvents(); // Re-added global modal event listener
      this.bindFilterEvents();
      this.renderTagFilters();
      this.initCoverStep();
      this.renderProducts('interior', 'interior-products-grid');
      this.renderProducts('accessory', 'accessory-products-grid');
      this.updateProductCardSelection();
      this.updateProgress();
      this.updateUI();
      this.updateEngravingSummary();
      this.bindRingSelectionEvents();
    }

    // ─── Cover Step ────────────────────────────────────────────────────────────

    getCoversByType(type) {
      const seen = new Set();
      return this.products.filter(p => {
        if (p.category !== 'core') return false;
        if (!p.available) return false;
        if (type && p.type !== type) return false;
        if (seen.has(p.colorHex)) return false;
        seen.add(p.colorHex);
        return true;
      });
    }

    _getRingTypeFromVariantTitle(title) {
      const t = String(title || '').toLowerCase();
      if (t.includes('plateadas') || t.includes('silver')) return 'silver';
      if (t.includes('doradas') || t.includes('gold')) return 'gold';
      return null;
    }

    getAvailableRings(cover) {
      if (!cover || !cover.variants || !cover.variants.length) return [];
      const rings = new Set();
      cover.variants.forEach(v => {
        const ringType = this._getRingTypeFromVariantTitle(v.title);
        if (ringType && v.available) rings.add(ringType);
      });
      return Array.from(rings);
    }

    updateRingOptions() {
      const cover = this.state.selectedCover;
      const availableRings = this.getAvailableRings(cover);
      const ringBlock = this.querySelector('#cover-ring-block');
      if (!ringBlock) return;

      if (availableRings.length === 0) {
        ringBlock.style.display = 'none';
        return;
      }

      ringBlock.style.display = '';

      ringBlock.querySelectorAll('[data-ring]').forEach(el => {
        const ring = el.dataset.ring;
        const isAvailable = availableRings.includes(ring);
        if (isAvailable) {
          el.style.display = '';
          el.classList.toggle('is-active', ring === this.state.ring);
          el.setAttribute('aria-checked', ring === this.state.ring ? 'true' : 'false');
        } else {
          el.style.display = 'none';
        }
      });

      if (!availableRings.includes(this.state.ring)) {
        this.setState({ ring: availableRings[0] });
      }
    }

    initCoverStep() {
      this.updateCoverTypeButtons();
      this.renderCoverColorOptions();
      const covers = this.getCoversByType(this.state.coverType);
      if (covers.length > 0) {
        this.selectCover(covers[0].colorHex);
      }
    }

    renderCoverColorOptions() {
      const container = this.querySelector('#color-options');
      if (!container) return;

      const covers = this.getCoversByType(this.state.coverType);

      container.setAttribute('role', 'radiogroup');
      container.setAttribute('aria-label', t('select_color', 'Selecciona un color'));

      if (covers.length === 0) {
        const noColorsText = t('no_colors_available', 'No colors available for this type.');
        container.innerHTML = `<p class="text-subdued text-sm">${noColorsText}</p>`;
        return;
      }

      container.innerHTML = covers.map(cover => {
        const hex = safeColorHex(cover.colorHex);
        const isActive = cover.colorHex === this.state.selectedColor;
        return `
          <button type="button" class="color-swatch rounded-full ${isActive ? 'is-active' : ''}"
                  data-color="${escHtml(cover.colorHex)}"
                  style="--swatch-background: ${hex};"
                  role="radio"
                  aria-checked="${isActive}"
                  aria-label="${escHtml(cover.title)}"
                  tabindex="${isActive ? '0' : '-1'}"></button>`;
      }).join('');

      // Bind the keydown handler ONCE (container node persists across re-renders).
      // Re-binding on every render would stack duplicate listeners and fire
      // handleColorSelect multiple times per arrow keypress.
      if (!this._colorKeyNavBound) {
        container.addEventListener('keydown', this._colorKeyNav);
        this._colorKeyNavBound = true;
      }
    }

    _colorKeyNav = (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const container = e.currentTarget;
      const swatches = Array.from(container.querySelectorAll('.color-swatch[role="radio"]'));
      if (!swatches.length) return;
      const idx = swatches.findIndex(s => s.getAttribute('tabindex') === '0');
      let nextIdx = idx;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIdx = (idx + 1) % swatches.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIdx = (idx - 1 + swatches.length) % swatches.length;
      if (nextIdx !== idx) {
        e.preventDefault();
        swatches[nextIdx].focus();
        this.handleColorSelect(swatches[nextIdx]);
      }
    }

    populateGallery(cover) {
      const imagesContainer = this.querySelector('#cover-gallery-carousel');
      const thumbsContainer = this.querySelector('#cover-gallery-thumbnails');
      const dotsContainer = this.querySelector('#cover-gallery-dots');

      if (!imagesContainer || !thumbsContainer) return;

      const allImages = (cover.images && cover.images.length) ? cover.images : [cover.image, cover.secondaryImage].filter(Boolean);

      if (dotsContainer) {
        dotsContainer.innerHTML = allImages.map((_, i) => {
          const isCurrent = i === 0 ? 'true' : 'false';
          return `
            <button type="button" class="product-gallery__thumbnail gallery-dot" aria-current="${isCurrent}" data-media-position="${i + 1}"></button>`;
        }).join('');
      }

      thumbsContainer.innerHTML = allImages.map((src, i) => {
        const isCurrent = i === 0 ? 'true' : 'false';
        return `
          <button type="button" class="product-gallery__thumbnail" aria-current="${isCurrent}"
                  data-media-position="${i + 1}" data-media-id="gallery-${i}" aria-label="Ir a imagen ${i + 1}">
            <img src="${escHtml(src)}" alt="${escHtml(cover.title)} - Thumbnail ${i + 1}" width="56" height="56" sizes="56px" loading="lazy" class="object-contain">
          </button>`;
      }).join('');

      this._activeGallery = { imagesContainer, images: allImages };
      this._bindGalleryInteractions(thumbsContainer);
      this._bindMainSwipe();

      this._replaceGalleryImage(imagesContainer, allImages, 0);
    }

    _bindGalleryInteractions(thumbsContainer) {
      const list = thumbsContainer;
      const scroller = list;

      if (!scroller) return;

      if (this._coverThumbnailList) {
        this._coverThumbnailList.removeEventListener('click', this._handleGalleryClick, true);
        this._coverThumbnailList.removeEventListener('mousedown', this._handleGalleryMouseDown);
        this._coverThumbnailList.removeEventListener('touchstart', this._handleGalleryTouchStart);
        this._coverThumbnailList.removeEventListener('touchmove', this._handleGalleryTouchMove);
        this._coverThumbnailList.removeEventListener('touchend', this._handleGalleryTouchEnd);
        this._coverThumbnailList.removeEventListener('touchcancel', this._handleGalleryTouchEnd);
      }

      this._coverThumbnailList = list;
      this._galleryDrag.scroller = scroller;
      this._galleryDrag.suppressClickUntil = 0;

      list.addEventListener('click', this._handleGalleryClick, true);
      list.addEventListener('mousedown', this._handleGalleryMouseDown);
      list.addEventListener('touchstart', this._handleGalleryTouchStart, { passive: true });
      list.addEventListener('touchmove', this._handleGalleryTouchMove, { passive: true });
      list.addEventListener('touchend', this._handleGalleryTouchEnd);
      list.addEventListener('touchcancel', this._handleGalleryTouchEnd);
    }

    handleDotClick(dot) {
      const activeGallery = this._activeGallery;
      if (!activeGallery || !activeGallery.imagesContainer || !activeGallery.images.length) return;

      const idx = parseInt(dot.dataset.mediaPosition, 10) - 1;
      if (idx < 0 || idx >= activeGallery.images.length) return;

      this._replaceGalleryImage(activeGallery.imagesContainer, activeGallery.images, idx);

      const isModal = activeGallery.imagesContainer.id === 'modal-gallery-carousel';
      const dotsContainer = document.querySelector(isModal ? '#modal-gallery-dots-thumbs' : '#cover-gallery-dots');
      const thumbsContainer = document.querySelector(isModal ? '#modal-gallery-thumbnails' : '#cover-gallery-thumbnails');

      (dotsContainer?.querySelectorAll('.gallery-dot') || []).forEach(d => {
        d.setAttribute('aria-current', d.dataset.mediaPosition == idx + 1 ? 'true' : 'false');
      });
      (thumbsContainer?.querySelectorAll('.product-gallery__thumbnail') || []).forEach(thumb => {
        thumb.setAttribute('aria-current', thumb.dataset.mediaPosition == idx + 1 ? 'true' : 'false');
      });
    }

    _handleGalleryClick(event) {
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      if (!target) return;

      if (Date.now() < this._galleryDrag.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (target.closest('.thumbnail-scroll-btn--up')) {
        const scroller = this._getGalleryScroller();
        if (scroller) scroller.scrollBy({ top: -120, behavior: 'smooth' });
        return;
      }

      if (target.closest('.thumbnail-scroll-btn--down')) {
        const scroller = this._getGalleryScroller();
        if (scroller) scroller.scrollBy({ top: 120, behavior: 'smooth' });
        return;
      }

      if (target.closest('.thumbnail-scroll-btn--left')) {
        const scroller = this._getGalleryScroller();
        if (scroller) scroller.scrollBy({ left: -120, behavior: 'smooth' });
        return;
      }

      if (target.closest('.thumbnail-scroll-btn--right')) {
        const scroller = this._getGalleryScroller();
        if (scroller) scroller.scrollBy({ left: 120, behavior: 'smooth' });
        return;
      }

      const thumb = target.closest('.product-gallery__thumbnail');
      if (!thumb) return;

      const activeGallery = this._activeGallery;
      if (!activeGallery || !activeGallery.imagesContainer || !activeGallery.images.length) return;

      const idx = parseInt(thumb.getAttribute('data-media-position'), 10) - 1;
      if (idx < 0 || idx >= activeGallery.images.length) return;

      this._replaceGalleryImage(activeGallery.imagesContainer, activeGallery.images, idx);
      const scroller = thumb.closest('.product-gallery__thumbnail-scroller');
      (scroller?.querySelectorAll('.product-gallery__thumbnail') || []).forEach((thumbnail, i) => {
        thumbnail.setAttribute('aria-current', thumbnail === thumb ? 'true' : 'false');
      });
    }

    _handleGalleryMouseDown(event) {
      if (event.button !== 0) return;

      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('.thumbnail-scroll-btn')) return;

      const scroller = this._getGalleryScroller();
      if (!scroller) return;

      this._galleryDrag.isMouseDown = true;
      this._galleryDrag.isDragging = false;
      this._galleryDrag.startY = event.clientY;
      this._galleryDrag.lastY = event.clientY;
      this._galleryDrag.startScrollTop = scroller.scrollTop;
      this._galleryDrag.velocityY = 0;
      this._galleryDrag.lastTime = performance.now();

      window.addEventListener('mousemove', this._handleGalleryMouseMove);
      window.addEventListener('mouseup', this._handleGalleryMouseUp);
    }

    _handleGalleryMouseMove(event) {
      const drag = this._galleryDrag;
      if (!drag.isMouseDown) return;

      const scroller = this._getGalleryScroller();
      if (!scroller) {
        this._stopGalleryMouseDrag();
        return;
      }

      const deltaY = event.clientY - drag.startY;
      if (!drag.isDragging && Math.abs(deltaY) < 5) return;

      if (!drag.isDragging) {
        drag.isDragging = true;
        drag.suppressClickUntil = Date.now() + 450;
        scroller.classList.add('is-dragging');
      }

      event.preventDefault();

      const now = performance.now();
      const delta = Math.max(now - drag.lastTime, 1);
      drag.velocityY = (event.clientY - drag.lastY) / delta;
      drag.lastY = event.clientY;
      drag.lastTime = now;
      scroller.scrollTop = drag.startScrollTop - deltaY;
    }

    _handleGalleryMouseUp() {
      const drag = this._galleryDrag;
      if (!drag.isMouseDown) return;

      const wasDragging = drag.isDragging;
      this._stopGalleryMouseDrag();

      const scroller = this._getGalleryScroller();
      if (wasDragging) {
        this._animateGalleryMomentum(scroller, drag.velocityY);
      }
    }

    _stopGalleryMouseDrag() {
      const drag = this._galleryDrag;
      window.removeEventListener('mousemove', this._handleGalleryMouseMove);
      window.removeEventListener('mouseup', this._handleGalleryMouseUp);
      drag.isMouseDown = false;

      const scroller = this._getGalleryScroller();
      if (scroller) scroller.classList.remove('is-dragging');
      drag.isDragging = false;
    }

    _handleGalleryTouchStart(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('.thumbnail-scroll-btn')) return;

      const touch = event.changedTouches[0];
      const scroller = this._getGalleryScroller();
      if (!touch || !scroller) return;

      const now = performance.now();
      this._galleryDrag.touchStart = {
        startY: touch.clientY,
        lastY: touch.clientY,
        startTime: now,
        lastTime: now,
        moved: false
      };
      scroller.classList.add('is-dragging');
    }

    _handleGalleryTouchMove(event) {
      const touchState = this._galleryDrag.touchStart;
      if (!touchState) return;

      const touch = event.changedTouches[0];
      if (!touch) return;

      if (Math.abs(touch.clientY - touchState.startY) > 3) {
        touchState.moved = true;
        this._galleryDrag.suppressClickUntil = Date.now() + 450;
      }

      touchState.lastY = touch.clientY;
      touchState.lastTime = performance.now();
    }

    _handleGalleryTouchEnd() {
      const touchState = this._galleryDrag.touchStart;
      const scroller = this._getGalleryScroller();
      if (scroller) scroller.classList.remove('is-dragging');

      if (touchState?.moved) {
        this._galleryDrag.suppressClickUntil = Date.now() + 450;
      }

      this._galleryDrag.touchStart = null;
      this._reviewsLoadedForHandle = null;
      this._reviewsContainerSelector = null;
    }

    _handleMainTouchStart(event) {
      if (event.touches.length !== 1) return;
      this._mainSwipe.startX = event.touches[0].clientX;
      this._mainSwipe.startTime = Date.now();
    }

    _handleMainTouchEnd(event) {
      const swipe = this._mainSwipe;
      const activeGallery = this._activeGallery;
      if (!swipe.startX || !activeGallery || !activeGallery.images.length) return;

      const touch = event.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - swipe.startX;
      const deltaTime = Date.now() - swipe.startTime;

      if (Math.abs(deltaX) < 50 || deltaTime > 500) {
        swipe.startX = 0;
        return;
      }

      const imagesContainer = activeGallery.imagesContainer;
      const currentMedia = imagesContainer.querySelector('.product-gallery__media');
      const currentIdx = currentMedia ? parseInt(currentMedia.getAttribute('data-media-id')?.replace('gallery-', '') || '0', 10) : 0;
      const total = activeGallery.images.length;

      let nextIdx = currentIdx;
      if (deltaX < 0 && currentIdx < total - 1) {
        nextIdx = currentIdx + 1;
      } else if (deltaX > 0 && currentIdx > 0) {
        nextIdx = currentIdx - 1;
      }

      if (nextIdx !== currentIdx) {
        const isModal = imagesContainer.id === 'modal-gallery-carousel';
        const dotsSelector = isModal ? '#modal-gallery-dots-thumbs' : '#cover-gallery-dots';
        const dotsContainer = document.querySelector(dotsSelector);
        const dot = dotsContainer?.querySelector(`.gallery-dot[data-media-position="${nextIdx + 1}"]`);
        if (dot) this.handleDotClick(dot);
      }

      swipe.startX = 0;
    }

    _bindCarouselSwipe(carousel) {
      if (!carousel) return;

      // Cache bound handlers once so we can removeEventListener before re-adding.
      // Prevents handler accumulation (and double-firing) every time the modal opens.
      if (!this._boundSwipeHandlers) {
        this._boundSwipeHandlers = {
          touchStart: this._handleMainTouchStart.bind(this),
          touchEnd: this._handleMainTouchEnd.bind(this),
          mouseDown: this._handleCarouselMouseDown.bind(this)
        };
      }

      carousel.removeEventListener('touchstart', this._boundSwipeHandlers.touchStart);
      carousel.removeEventListener('touchend', this._boundSwipeHandlers.touchEnd);
      carousel.removeEventListener('mousedown', this._boundSwipeHandlers.mouseDown);

      carousel.addEventListener('touchstart', this._boundSwipeHandlers.touchStart, { passive: true });
      carousel.addEventListener('touchend', this._boundSwipeHandlers.touchEnd);
      carousel.addEventListener('mousedown', this._boundSwipeHandlers.mouseDown);
    }

    _handleCarouselMouseDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      this._carouselMouseDrag = { startX: event.clientX, bound: true };
      document.addEventListener('mousemove', this._boundCarouselMouseMove);
      document.addEventListener('mouseup', this._boundCarouselMouseUp);
    }

    _handleCarouselMouseMove(event) {
      if (!this._carouselMouseDrag || !this._carouselMouseDrag.bound) return;

      const deltaX = event.clientX - this._carouselMouseDrag.startX;
      const threshold = 50;

      if (Math.abs(deltaX) > threshold) {
        if (deltaX < 0) {
          this._navigateGalleryNext();
        } else {
          this._navigateGalleryPrev();
        }
        this._handleCarouselMouseUp(event);
      }
    }

    _navigateGalleryNext() {
      const activeGallery = this._activeGallery;
      if (!activeGallery || !activeGallery.images.length) return;
      const imagesContainer = activeGallery.imagesContainer;
      const currentMedia = imagesContainer.querySelector('.product-gallery__media');
      const currentIdx = currentMedia ? parseInt(currentMedia.getAttribute('data-media-id')?.replace('gallery-', '') || '0', 10) : 0;
      const total = activeGallery.images.length;
      if (currentIdx < total - 1) {
        const isModal = imagesContainer.id === 'modal-gallery-carousel';
        const dotsSelector = isModal ? '#modal-gallery-dots-thumbs' : '#cover-gallery-dots';
        const dotsContainer = document.querySelector(dotsSelector);
        const dot = dotsContainer?.querySelector(`.gallery-dot[data-media-position="${currentIdx + 2}"]`);
        if (dot) this.handleDotClick(dot);
      }
    }

    _navigateGalleryPrev() {
      const activeGallery = this._activeGallery;
      if (!activeGallery || !activeGallery.images.length) return;
      const imagesContainer = activeGallery.imagesContainer;
      const currentMedia = imagesContainer.querySelector('.product-gallery__media');
      const currentIdx = currentMedia ? parseInt(currentMedia.getAttribute('data-media-id')?.replace('gallery-', '') || '0', 10) : 0;
      if (currentIdx > 0) {
        const isModal = imagesContainer.id === 'modal-gallery-carousel';
        const dotsSelector = isModal ? '#modal-gallery-dots-thumbs' : '#cover-gallery-dots';
        const dotsContainer = document.querySelector(dotsSelector);
        const dot = dotsContainer?.querySelector(`.gallery-dot[data-media-position="${currentIdx}"]`);
        if (dot) this.handleDotClick(dot);
      }
    }

    _handleCarouselMouseUp(event) {
      if (!this._carouselMouseDrag || !this._carouselMouseDrag.bound) return;
      document.removeEventListener('mousemove', this._boundCarouselMouseMove);
      document.removeEventListener('mouseup', this._boundCarouselMouseUp);
      this._carouselMouseDrag = { bound: false };
    }

    _bindMainSwipe() {
      const carousel = this.querySelector('#cover-gallery-carousel');
      if (!carousel || this._mainSwipe.bound) return;

      this._bindCarouselSwipe(carousel);
      this._mainSwipe.bound = true;
    }

    _previewRingInGallery(ringColor) {
      const imagesContainer = this.querySelector('#cover-gallery-carousel');
      const activeGallery = this._activeGallery;
      if (!imagesContainer || !activeGallery || !activeGallery.images.length) return;

      const allImages = activeGallery.images;
      const matchStr = ringColor + '_ring';

      // Filter available image sources to find one whose src contains the selected ring color
      const matchIndex = allImages.findIndex(src => String(src).includes(matchStr));

      // Null Handling - If no matching image is found, do absolutely nothing (prevent carousel from sliding or breaking)
      if (matchIndex === -1) return;

      // Update the carousel to display the matching ring image
      this._replaceGalleryImage(imagesContainer, allImages, matchIndex);
    }

    _getGalleryScroller() {
      const scroller = this._galleryDrag.scroller;
      if (scroller && scroller.isConnected) return scroller;

      const thumbsContainer = this.querySelector('#cover-gallery-thumbnails');
      if (!thumbsContainer) return null;

      const nextScroller = thumbsContainer.classList.contains('product-gallery__thumbnail-scroller')
        ? thumbsContainer
        : thumbsContainer.querySelector('.product-gallery__thumbnail-scroller');

      this._galleryDrag.scroller = nextScroller;
      return nextScroller;
    }

    _animateGalleryMomentum(scroller, velocityY) {
      if (!scroller || (scroller.scrollHeight <= scroller.clientHeight && scroller.scrollWidth <= scroller.clientWidth)) return;

      cancelAnimationFrame(this._galleryDrag.rafId);

      const maxVelocity = 2.2;
      const minVelocity = 0.12;
      let currentVelocity = Math.max(-maxVelocity, Math.min(maxVelocity, velocityY || 0));

      if (Math.abs(currentVelocity) < minVelocity) return;

      const step = () => {
        if (Math.abs(currentVelocity) < minVelocity) return;

        const previousScrollTop = scroller.scrollTop;
        scroller.scrollTop -= currentVelocity;

        if (scroller.scrollTop === previousScrollTop) return;

        currentVelocity *= 0.92;
        this._galleryDrag.rafId = requestAnimationFrame(step);
      };

      this._galleryDrag.rafId = requestAnimationFrame(step);
    }

    _replaceGalleryImage(container, images, index) {
      if (!images.length) return;
      const src = images[index];
      const media = container.querySelector('.product-gallery__media');
      const isInitialBuild = !media;

      if (isInitialBuild) {
        container.innerHTML = `
          <div class="product-gallery__media snap-center is-initial" data-media-type="image" data-media-id="gallery-${index}" role="group" aria-label="Imagen ${index + 1}">
            <img src="${escHtml(src)}" alt="" loading="eager" width="600" height="800" fetchpriority="high" sizes="(max-width: 699px) calc(100vw - 40px), (max-width: 999px) calc(100vw - 64px), min(1100px, 819px - 96px)">
          </div>`;
        return;
      }

      const img = media.querySelector('img');
      if (!img) return;

      media.setAttribute('data-media-id', `gallery-${index}`);
      media.setAttribute('aria-label', `Imagen ${index + 1}`);

      if (img.src !== src) {
        img.style.opacity = '0';
        img.style.transition = 'opacity 0.3s ease';
        const fresh = new Image();
        fresh.onload = () => {
          img.src = src;
          requestAnimationFrame(() => { img.style.opacity = '1'; });
        };
        fresh.onerror = () => { img.style.opacity = '1'; };
        fresh.src = src;
      }
    }

    updateProductInfo(cover) {
      const titleEl = this.querySelector('#cover-product-title');
      const priceEl = this.querySelector('#cover-product-price');
      const hiddenPriceEl = this.querySelector('#cover-price-hidden');
      const priceListEl = this.querySelector('#cover-price-list');
      const descriptionEl = this.querySelector('#cover-description-content');
      const includesEl = this.querySelector('#cover-includes-content');

      if (titleEl) titleEl.textContent = cover.title;
      if (priceEl) priceEl.textContent = cover.priceFormatted;
      if (hiddenPriceEl) hiddenPriceEl.textContent = cover.priceFormatted;
      if (priceListEl) priceListEl.hidden = false;
      if (descriptionEl) descriptionEl.innerHTML = cover.description || '';
      if (includesEl) includesEl.innerHTML = (cover.bundleInclude || '').replace(/\n/g, '<br>');
    }

    selectCover(colorHex) {
      const cover = this.products.find(p => p.category === 'core' && p.type === this.state.coverType && p.colorHex === colorHex);
      if (!cover) return;

      this.state.selectedColor = colorHex;
      this.state.selectedCover = cover;

      this.updateProductInfo(cover);
      this.populateGallery(cover);
      this.refreshProductReviews(cover.handle, '#bundle-reviews-container');
      this.drawEngravingPreview(
        (document.querySelector('#engraving-input')?.value ||
          document.querySelector('#engraving-input-mobile')?.value ||
          this.state.engraving || '')
      );
      this.updateEngravingSummary();
      this.updateSummary();
      this.updateRingOptions();

      this.querySelectorAll('#color-options [data-color]').forEach(swatch => {
        swatch.classList.toggle('is-active', swatch.dataset.color === colorHex);
      });
    }

    _extractReviewData(htmlFragment) {
      try {
        const marker = 'jdgm.data.reviewWidget[';
        const startIdx = String(htmlFragment).indexOf(marker);
        if (startIdx === -1) return null;
        const eqIdx = String(htmlFragment).indexOf('=', startIdx);
        if (eqIdx === -1) return null;
        const bodyStart = String(htmlFragment).indexOf('{', eqIdx);
        if (bodyStart === -1) return null;

        let depth = 0;
        let inStr = false;
        let strChar = '';
        let endIdx = -1;
        const str = String(htmlFragment);
        for (let i = bodyStart; i < str.length; i++) {
          const ch = str[i];
          if (inStr) {
            if (ch === '\\') { i++; continue; }
            if (ch === strChar) inStr = false;
            continue;
          }
          if (ch === '"' || ch === "'") { inStr = true; strChar = ch; continue; }
          if (ch === '{') depth++;
          else if (ch === '}') {
            depth--;
            if (depth === 0) { endIdx = i; break; }
          }
        }

        if (endIdx === -1) return null;
        return JSON.parse(str.substring(bodyStart, endIdx + 1));
      } catch (e) {
        return null;
      }
    }

    _buildStarHtml(rating) {
      const r = Math.round(Math.min(5, Math.max(0, Number(rating) || 0)));
      return '★'.repeat(r) + '☆'.repeat(5 - r);
    }

    _buildReviewsReport(root, data, productHandle) {
      const avg = Number(data.average_rating || 0);
      const avgFixed = Number(avg.toFixed(1));
      const count = Number(data.number_of_reviews || data.reviews.length);
      const starsHtml = this._buildStarHtml(avgFixed);
      const hist = (data.histogram || []).slice().sort((a, b) => b.rating - a.rating);
      const maxFreq = hist.reduce((m, h) => Math.max(m, Number(h.frequency || 0)), 0) || 1;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (isMobile) {
        const topRow = document.createElement('div');
        topRow.innerHTML = `
          <span class="h6" style="text-align:center;">${t('reviews', 'Reseñas')}</span>
          <span style="color:#000; font-size:18px; letter-spacing:2px; line-height:1;">★★★★★</span>
        `;
        root.appendChild(topRow);
        return;
      }

      const topRow = document.createElement('div');
      topRow.style.cssText = 'display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:16px; margin-bottom:16px;';
      topRow.innerHTML = `
        <span class="h6">${t('reviews', 'Reseñas')}</span>
        <div class="h-stack gap-3 items-center">
          <span style="font-size:48px; font-weight:500; line-height:1;">${avgFixed.toFixed(1)}</span>
          <div class="v-stack gap-1" style="line-height:1.2;">
            <span class="review-stars" style="color:#f5a623; font-size:18px; letter-spacing:2px;">${starsHtml}</span>
            <span style="font-size:13px; color:var(--text-color-subdued,#666);">${t('based_on', 'Basado en')} ${count} ${t('opinions', 'opiniones')}</span>
          </div>
        </div>
      `;
      root.appendChild(topRow);

      const bottomRow = document.createElement('div');
      bottomRow.style.cssText = 'display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:16px; margin-bottom:24px;';

      const histogramContainer = document.createElement('div');
      histogramContainer.className = 'custom-reviews__histogram v-stack';
      histogramContainer.style.cssText = 'gap:6px; flex:1; max-width:350px;';
      hist.forEach(h => {
        const freq = Number(h.frequency || 0);
        const pct = maxFreq ? (freq / maxFreq) * 100 : 0;
        const row = document.createElement('div');
        row.className = 'h-stack items-center';
        row.style.cssText = 'display:inline-flex; align-items:center; gap:10px; width:100%;';
        row.innerHTML = `
          <span style="display:inline-flex; align-items:center; gap:2px; width:auto; text-align:right; font-size:14px; font-weight:500;">${h.rating} <span style="color:#000; line-height:1;">★</span></span>
          <div style="flex:1; min-width:0; height:10px; background:#e5e5e5; border-radius:0 3px 3px 0; overflow:hidden;">
            <div style="width:${pct}%; background:#000; height:100%; border-radius:0 3px 3px 0;"></div>
          </div>
          <span style="width:24px; font-size:14px; color:var(--text-color-subdued,#666); text-align:left;">${freq}</span>
        `;
        histogramContainer.appendChild(row);
      });
      bottomRow.appendChild(histogramContainer);

      const writeReviewBtn = document.createElement('a');
      writeReviewBtn.href = `/products/${encodeURIComponent((productHandle).toLowerCase().replace(/[^a-z0-9]+/g, '-'))}?#:~:text=Write%20a%20review`;
      writeReviewBtn.className = 'button button--outline';
      writeReviewBtn.style.cssText = 'background:#1b7f6e; color:#fff; border-color:#1b7f6e; padding:10px 20px; font-size:14px; text-decoration:none;';
      writeReviewBtn.textContent = t('write_review', 'Escribe una opinión');
      bottomRow.appendChild(writeReviewBtn);

      root.appendChild(bottomRow);
    }

    _buildReviewsCarousel(root, data) {
      const reviews = data.reviews || [];
      if (!reviews.length) return;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (isMobile) {
        const list = document.createElement('div');
        list.className = 'custom-reviews__list';

        reviews.forEach(r => {
          const item = document.createElement('div');
          item.className = 'custom-reviews__list-item';
          const rStars = this._buildStarHtml(r.rating);
          const dateObj = r.created_at ? new Date(r.created_at) : null;
          const dateStr = dateObj
            ? `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`
            : '';
          item.innerHTML = `
            <div style="display:flex; align-items:center; gap:8px; margin-bottom:6px;">
              <span style="color:#000; letter-spacing:1px; font-size:14px; line-height:1;">${rStars}</span>
              ${dateStr ? `<span style="color:var(--text-color-subdued,#666); font-size:13px;">${dateStr}</span>` : ''}
            </div>
            ${r.title ? `<div style="font-weight:500; font-size:15px; line-height:1.35; margin-bottom:4px;">${this._escHtml(r.title)}</div>` : ''}
            <p style="font-size:14px; margin:0; line-height:1.6; color:var(--text-color-subdued,#666);">${this._escHtml(r.body || '')}</p>
          `;
          list.appendChild(item);
        });

        root.appendChild(list);
        return;
      }

      const visibleCount = 3;
      const currentIndex = { value: 0 };

      const carousel = document.createElement('div');
      carousel.className = 'custom-reviews__carousel';

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'custom-reviews__arrow custom-reviews__arrow--prev';
      prevBtn.setAttribute('aria-label', 'Previous reviews');
      prevBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'custom-reviews__arrow custom-reviews__arrow--next';
      nextBtn.setAttribute('aria-label', 'Next reviews');
      nextBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';

      const track = document.createElement('div');
      track.className = 'custom-reviews__track';

      reviews.forEach(r => {
        const card = document.createElement('div');
        card.className = 'custom-reviews__card';
        card.style.cssText = 'border:1px solid #d9d9d9; padding:16px; display:flex; flex-direction:column; gap:8px; flex:0 0 calc((100% - 32px) / 3); min-width:0;';
        const rStars = this._buildStarHtml(r.rating);
        const dateObj = r.created_at ? new Date(r.created_at) : null;
        const dateStr = dateObj
          ? `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`
          : '';
        const reviewer = (r.reviewer_name || '').trim() || 'Customer';
        const shortName = reviewer.length > 12 ? reviewer.split(' ').map((n, i) => i === 0 ? n : n[0] + '.').join(' ') : reviewer;
        card.innerHTML = `
          <div style="display:flex; align-items:center; gap:8px; margin-bottom:4px;">
            <span style="color:#f5a623; letter-spacing:1px; font-size:14px;">${rStars}</span>
            <span style="color:var(--text-color-subdued,#666); font-size:13px;">${dateStr}</span>
          </div>
          <div style="font-weight:500; font-size:15px; line-height:1.3;">${this._escHtml(r.title || '')}</div>
          <p style="font-size:14px; margin:0; line-height:1.5; flex:1;">${this._escHtml(r.body || '')}</p>
          <div style="font-size:14px; font-weight:500; margin-top:8px;">${this._escHtml(shortName)}</div>
        `;
        track.appendChild(card);
      });

      const scrollByCards = (dir) => {
        const maxIndex = Math.max(0, reviews.length - visibleCount);
        let newIndex = currentIndex.value + dir;
        const isWrap = newIndex < 0 || newIndex > maxIndex;
        if (newIndex < 0) newIndex = maxIndex;
        else if (newIndex > maxIndex) newIndex = 0;
        currentIndex.value = newIndex;
        const cardWidth = track.firstChild ? track.firstChild.getBoundingClientRect().width : 0;
        const gap = 16;
        track.scrollTo({ left: newIndex * (cardWidth + gap), behavior: isWrap ? 'auto' : 'smooth' });
      };

      prevBtn.addEventListener('click', () => scrollByCards(-1));
      nextBtn.addEventListener('click', () => scrollByCards(1));

      carousel.appendChild(prevBtn);
      carousel.appendChild(track);
      carousel.appendChild(nextBtn);
      root.appendChild(carousel);
    }


    _escHtml(value) {
      return String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    updateProductReviews(productHandle, containerSelector) {
      let container;
      if (typeof containerSelector === 'string') {
        container = this.querySelector(containerSelector) || document.querySelector(containerSelector);
      } else {
        container = containerSelector;
      }
      if (!container || !productHandle) {
        return Promise.resolve();
      }

      // Cancel any in-flight review request so a stale cover can't render over a newer one.
      if (this._reviewsAbortController) {
        this._reviewsAbortController.abort();
      }
      this._reviewsAbortController = new AbortController();

      return fetch(`/products/${productHandle}?section_id=builder-reviews`, {
        signal: this._reviewsAbortController.signal
      })
        .then(response => {
          if (!response.ok) throw new Error(`Reviews fetch failed: ${response.status}`);
          return response.text();
        })
        .then(htmlFragment => {
          if (!htmlFragment || !htmlFragment.trim()) throw new Error('Empty reviews response');
          const data = this._extractReviewData(htmlFragment);
          if (!data || !data.reviews || !data.reviews.length) {
            container.innerHTML = '<p class="text-subdued">No reviews yet.</p>';
            return;
          }

          const filteredReviews = data.reviews.filter(r => Number(r.rating) >= 3);
          if (!filteredReviews.length) {
            container.innerHTML = '<p class="text-subdued">No reviews yet.</p>';
            return;
          }
          data.reviews = filteredReviews;
          data.number_of_reviews = filteredReviews.length;
          data.average_rating = filteredReviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / filteredReviews.length;
          const histMap = {};
          filteredReviews.forEach(r => {
            const rating = Math.round(Number(r.rating) || 0);
            if (rating >= 1 && rating <= 5) {
              histMap[rating] = (histMap[rating] || 0) + 1;
            }
          });
          data.histogram = Object.keys(histMap)
            .map(rating => ({ rating: Number(rating), frequency: histMap[rating] }))
            .sort((a, b) => b.rating - a.rating);

          container.innerHTML = '';
          const root = document.createElement('div');
          root.className = 'custom-reviews';
          this._buildReviewsReport(root, data, productHandle);
          this._buildReviewsCarousel(root, data);
          container.appendChild(root);
        })
        .catch(error => {
          if (error.name === 'AbortError') return; // Expected when a newer request supersedes this one
          console.error('Error loading Judge.me reviews:', error);
        });
    }

    refreshProductReviews(productHandle, containerSelector) {
      this.updateProductReviews(productHandle, containerSelector);
    }

    bindEvents() {
      // Local events (Inside <bundle-configurator>)
      this.addEventListener('click', (e) => {
        const target = e.target;

        if (target.closest('.stepper__item')) return this.handleStepClick(target.closest('.stepper__item'));
        if (target.closest('.block-swatch')) return this.handleTypeSelect(target.closest('.block-swatch'));
        if (target.closest('[data-ring]')) return this.handleRingSelect(target.closest('[data-ring]'));
        if (target.closest('[data-color]')) return this.handleColorSelect(target.closest('[data-color]'));
        if (target.closest('.bundle-nav-back')) return this.handleNavBack(target.closest('.bundle-nav-back'));
        if (target.closest('.bundle-product-card__add-btn')) {
          const card = target.closest('.bundle-product-card');
          const pid = parseInt(card.dataset.productId);
          const product = this.products.find(p => p.id === pid);
          if (product && !product.available) return;
          return this.handleProductAdd(card);
        }
        if (target.closest('.bundle-product-card__qty-inc')) {
          const card = target.closest('.bundle-product-card');
          const pid = parseInt(card.dataset.productId);
          const product = this.products.find(p => p.id === pid);
          if (product && !product.available) return;
          return this.handleProductInc(card);
        }
        if (target.closest('.bundle-product-card__qty-dec')) return this.handleProductDec(target.closest('.bundle-product-card'));
        const card = target.closest('.bundle-product-card');
        if (card && !target.closest('.bundle-product-card__qty-control') && !target.closest('.bundle-product-card__add-btn')) {
          const pid = parseInt(card.dataset.productId);
          const product = this.products.find(p => p.id === pid);
          if (product && !product.available) return;
          if (target.closest('a')) e.preventDefault();
          return this.handleProductCardClick(card);
        }
        if (target.closest('.gallery-dot')) return this.handleDotClick(target.closest('.gallery-dot'));
        if (target.closest('.bundle-checkout')) return this.handleCheckout();
        if (target.closest('.popover__value-option[data-sort]')) return this.handleSortChange(target.closest('.popover__value-option[data-sort]'));

        if (target.id === 'step1-continue') return this.handleStepNext(1);
        if (target.id === 'step2-continue') return this.handleStepNext(2);
      });
    }

    cleanEngravingText(value) {
      let clean = String(value || '').replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñÜü&\. ]+/g, '');
      clean = clean.toUpperCase();
      return clean.substring(0, 15);
    }

    bindModalEvents() {
      // Global events (For <x-modal> elements located outside component)
      document.addEventListener('click', (e) => {
        const target = e.target;

        if (target.closest('#engraving-trigger')) return this.openEngravingModal();
        if (target.closest('#engraving-confirm')) return this.handleEngravingClose('engraving-confirm');
        if (target.closest('#engraving-save-next')) {
          this.handleEngravingClose('engraving-confirm');
          return this.handleStepNext(1);
        }
        if (target.closest('.engraving-checkout')) return this.handleCheckout();

        const engravingColor = target.closest('.engraving-color-options .color-swatch');
        if (engravingColor) {
          const color = engravingColor.dataset.color;
          this.state.engravingColor = isBlindEngravingColor(color) ? 'blind' : color;
          this.syncEngravingColorButtons();
          const input = document.querySelector('#engraving-input');
          this.drawEngravingPreview(input ? input.value : (this.state.engraving || ''));
          this.updateEngravingSummary();
          return;
        }

        const fontSwatch = target.closest('.engraving-font-options .font-swatch');
        if (fontSwatch) {
          this.state.engravingFont = fontSwatch.dataset.fontLabel;
          this.state.engravingFontFamily = fontSwatch.dataset.fontFamily;
          this.state.engravingFontWeight = parseInt(fontSwatch.style.getPropertyValue('--engraving-font-weight') || '500', 10);
          this.syncEngravingFontButtons();
          const input = document.querySelector('#engraving-input');
          this.drawEngravingPreview(input ? input.value : (this.state.engraving || ''));
          this.updateEngravingSummary();
          return;
        }

        if (target.closest('#modal-back-btn')) return this.closeProductDetailModal();
        if (target.closest('#modal-goto-step3-btn')) {
          this.closeProductDetailModal();
          this.setState({ currentStep: 3 });
          this.updateUI();
          return;
        }
        if (target.closest('#modal-add-to-cart')) {
          if (this._modalProduct && !this._modalProduct.available) return;
          this._setModalQuantity(this._modalLocalQty || 1);
          this.closeProductDetailModal();
          return;
        }
        if (target.closest('#modal-favorite-btn')) {
          this._toggleFavorite();
          return;
        }

        if (target.closest('#post-checkout-fresh')) {
          this.closePostCheckoutModal();
          this._resetBundleState();
          this.initCoverStep();
          this.renderProducts('interior', 'interior-products-grid');
          this.renderProducts('accessory', 'accessory-products-grid');
          this.updateProductCardSelection();
          this.updateSummary();
          this.updateProgress();
          this.updateUI();
          this.updateEngravingSummary();
          return;
        }
        if (target.closest('#post-checkout-same')) {
          this.closePostCheckoutModal();
          this.setState({ currentStep: 1 });
          this.updateUI();
          this.updateSummary();
          this.updateEngravingSummary();
          return;
        }
        if (target.closest('#post-checkout-checkout')) {
          this.closePostCheckoutModal();
          window.location.href = '/checkout';
          return;
        }
        if (target.closest('#post-checkout-explore')) {
          this.closePostCheckoutModal();
          const exploreLink = (window.AppTranslations && window.AppTranslations.explore_link) || '/collections/all';
          window.location.href = exploreLink;
          return;
        }
      });

      // Instant input listener for the canvas (desktop + mobile inputs)
      const desktopInput = document.querySelector('#engraving-input');
      const mobileInput = document.querySelector('#engraving-input-mobile');

      const engravingInputHandler = (e) => {
        const clean = this.cleanEngravingText(e.target.value);
        if (clean !== e.target.value) {
          e.target.value = clean;
        }
        this.state.engraving = clean;
        if (desktopInput && desktopInput !== e.target) {
          desktopInput.value = clean;
        }
        if (mobileInput && mobileInput !== e.target) {
          mobileInput.value = clean;
        }
        this.drawEngravingPreview(clean);
        this.updateEngravingSummary();
      };

      if (desktopInput) desktopInput.addEventListener('input', engravingInputHandler);
      if (mobileInput) mobileInput.addEventListener('input', engravingInputHandler);

      const qtyInc = document.querySelector('#modal-qty-control .bundle-product-card__qty-inc');
      const qtyDec = document.querySelector('#modal-qty-control .bundle-product-card__qty-dec');
      if (qtyInc) qtyInc.addEventListener('click', (e) => { e.stopPropagation(); this._handleModalInc(); });
      if (qtyDec) qtyDec.addEventListener('click', (e) => { e.stopPropagation(); this._handleModalDec(); });
    }

    bindRingSelectionEvents() {
      this.querySelectorAll('[data-ring]').forEach(el => {
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleRingSelect(el);
          }
        });
      });
    }

    syncEngravingFontButtons() {
      document.querySelectorAll('.engraving-font-options .font-swatch').forEach(swatch => {
        const isActive = swatch.dataset.fontFamily === this.state.engravingFontFamily;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-pressed', String(isActive));
      });
    }

    syncEngravingColorButtons() {
      document.querySelectorAll('.engraving-color-options .color-swatch').forEach(swatch => {
        const isActive = swatch.dataset.color === this.state.engravingColor;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-pressed', String(isActive));
      });
    }

    // ─── Filter Events ─────────────────────────────────────────────────────

    _updatePriceFilterRange() {
      const coverType = this.state.coverType;
      const interiorPrices = this.products.filter(p => p.category === 'interior' && (!p.type || p.type === coverType)).map(p => p.price || 0);
      const accessoryPrices = this.products.filter(p => p.category === 'accessory' && (!p.type || p.type === coverType)).map(p => p.price || 0);
      const interiorMax = interiorPrices.length ? Math.max(...interiorPrices) : 5000;
      const accessoryMax = accessoryPrices.length ? Math.max(...accessoryPrices) : 5000;

      const interiorSelectors = ['#bundle-price-min-2', '#bundle-price-max-2', '#bundle-price-slider-min-2', '#bundle-price-slider-max-2'];
      const accessorySelectors = ['#bundle-price-min-3', '#bundle-price-max-3', '#bundle-price-slider-min-3', '#bundle-price-slider-max-3'];

      const applyRange = (selectors, maxCents) => {
        const maxDollars = maxCents / 100;
        selectors.forEach(selector => {
          const el = this.querySelector(selector);
          if (!el) return;
          if (selector.includes('slider')) {
            el.max = maxCents;
            if (selector.includes('min')) {
              el.value = 0;
            } else {
              el.value = maxCents;
            }
          } else {
            el.max = maxDollars;
            if (selector.includes('min')) {
              el.value = 0;
            } else {
              el.value = maxDollars.toFixed(2).replace(/\.00$/, '');
            }
          }
        });
      };

      applyRange(interiorSelectors, interiorMax);
      applyRange(accessorySelectors, accessoryMax);
    }

    // Coalesce interior + accessory grid renders into a single animation frame to
    // avoid redundant synchronous re-layouts when several filters fire together.
    scheduleProductRender() {
      if (this._renderScheduled) return;
      this._renderScheduled = true;
      requestAnimationFrame(() => {
        this._renderScheduled = false;
        this.renderProducts('interior', 'interior-products-grid');
        this.renderProducts('accessory', 'accessory-products-grid');
      });
    }

    bindFilterEvents() {
      const pairs = [
        { minInput: '#bundle-price-min-2', maxInput: '#bundle-price-max-2', minSlider: '#bundle-price-slider-min-2', maxSlider: '#bundle-price-slider-max-2', step: '2' },
        { minInput: '#bundle-price-min-2-mobile', maxInput: '#bundle-price-max-2-mobile', minSlider: '#bundle-price-slider-min-2-mobile', maxSlider: '#bundle-price-slider-max-2-mobile', step: '2' },
        { minInput: '#bundle-price-min-3', maxInput: '#bundle-price-max-3', minSlider: '#bundle-price-slider-min-3', maxSlider: '#bundle-price-slider-max-3', step: '3' },
        { minInput: '#bundle-price-min-3-mobile', maxInput: '#bundle-price-max-3-mobile', minSlider: '#bundle-price-slider-min-3-mobile', maxSlider: '#bundle-price-slider-max-3-mobile', step: '3' }
      ];

      pairs.forEach(pair => {
        const minInput = this.querySelector(pair.minInput);
        const maxInput = this.querySelector(pair.maxInput);
        const minSlider = this.querySelector(pair.minSlider);
        const maxSlider = this.querySelector(pair.maxSlider);

        const applyFilter = () => {
          clearTimeout(this._filterTimeout);
          this._filterTimeout = setTimeout(() => {
            if (pair.step === '3') {
              this.state.accessoryPriceMin = minInput?.value || null;
              this.state.accessoryPriceMax = maxInput?.value || null;
            } else {
              this.state.interiorPriceMin = minInput?.value || null;
              this.state.interiorPriceMax = maxInput?.value || null;
            }
            this.renderProducts('interior', 'interior-products-grid');
            this.renderProducts('accessory', 'accessory-products-grid');
          }, 150);
        };

        const syncFromInput = () => {
          if (minSlider) minSlider.value = Math.round((Number(minInput?.value) || 0) * 100);
          if (maxSlider) maxSlider.value = Math.round((Number(maxInput?.value) || 0) * 100);
          applyFilter();
        };

        const syncFromSlider = () => {
          let minV = Number(minSlider?.value) || 0;
          let maxV = Number(maxSlider?.value) || 0;
          if (minV > maxV) {
            const tmp = minV; minV = maxV; maxV = tmp;
            if (minSlider) minSlider.value = minV;
            if (maxSlider) maxSlider.value = maxV;
          }
          if (minInput) minInput.value = (minV / 100).toFixed(2).replace(/\.00$/, '');
          if (maxInput) maxInput.value = (maxV / 100).toFixed(2).replace(/\.00$/, '');
          applyFilter();
        };

        if (minInput) minInput.addEventListener('input', syncFromInput);
        if (maxInput) maxInput.addEventListener('input', syncFromInput);
        if (minSlider) minSlider.addEventListener('input', syncFromSlider);
        if (maxSlider) maxSlider.addEventListener('input', syncFromSlider);
      });

      const inStockInputs = ['#bundle-in-stock-2', '#bundle-in-stock-2-mobile', '#bundle-in-stock-3', '#bundle-in-stock-3-mobile'];
      inStockInputs.forEach(selector => {
        const input = this.querySelector(selector);
        if (!input) return;

        input.addEventListener('change', () => {
          const step = input.id.slice(-1);
          if (step === '3') {
            this.state.accessoryInStockOnly = input.checked;
          } else {
            this.state.interiorInStockOnly = input.checked;
          }
          this.scheduleProductRender();
        });
      });

      this.querySelectorAll('.reset-filters-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          this.resetFilters(btn.dataset.step);
        });
      });
    }

    resetFilters(step) {
      const isStep3 = step === '3';

      const pairs = [
        { minInput: `#bundle-price-min-${step}`, maxInput: `#bundle-price-max-${step}`, minSlider: `#bundle-price-slider-min-${step}`, maxSlider: `#bundle-price-slider-max-${step}` },
        { minInput: `#bundle-price-min-${step}-mobile`, maxInput: `#bundle-price-max-${step}-mobile`, minSlider: `#bundle-price-slider-min-${step}-mobile`, maxSlider: `#bundle-price-slider-max-${step}-mobile` }
      ];

      pairs.forEach(pair => {
        const minInput = this.querySelector(pair.minInput);
        const maxInput = this.querySelector(pair.maxInput);
        const minSlider = this.querySelector(pair.minSlider);
        const maxSlider = this.querySelector(pair.maxSlider);

        const maxVal = maxInput ? maxInput.max * 100 : 5000;

        if (minInput) minInput.value = 0;
        if (maxInput) maxInput.value = (maxVal / 100).toFixed(2).replace(/\.00$/, '');
        if (minSlider) { minSlider.max = maxVal; minSlider.value = 0; }
        if (maxSlider) { maxSlider.max = maxVal; maxSlider.value = maxVal; }
      });

      if (isStep3) {
        this.state.accessoryPriceMin = null;
        this.state.accessoryPriceMax = null;
        this.state.accessorySelectedTags = [];
      } else {
        this.state.interiorPriceMin = null;
        this.state.interiorPriceMax = null;
        this.state.interiorSelectedTags = [];
      }

      document.querySelectorAll('.bundle-tag-filter input[type="checkbox"]').forEach(cb => {
        cb.checked = false;
      });

      const inStockIds = isStep3
        ? ['#bundle-in-stock-3', '#bundle-in-stock-3-mobile']
        : ['#bundle-in-stock-2', '#bundle-in-stock-2-mobile'];
      inStockIds.forEach(id => {
        const input = this.querySelector(id);
        if (input) input.checked = false;
      });

      if (isStep3) {
        this.state.accessoryInStockOnly = false;
      } else {
        this.state.interiorInStockOnly = false;
      }

      this.scheduleProductRender();
    }

    _getUniqueTags(category) {
      const tags = new Set();
      const coverType = this.state.coverType;
      this.products.forEach(p => {
        if (p.category !== category) return;
        if (p.type && p.type !== coverType) return;
        if (p.tags && Array.isArray(p.tags)) {
          p.tags.forEach(tag => {
            if (tag) tags.add(tag);
          });
        }
      });
      return Array.from(tags).sort((a, b) => a.localeCompare(b));
    }

    renderTagFilters() {
      ['interior', 'accessory'].forEach(category => {
        const mobileContainer = this.querySelector(`#${category}-tag-filters-mobile`);
        const desktopContainer = this.querySelector(`#${category}-tag-filters-desktop`);
        const tags = this._getUniqueTags(category);

        if (!tags.length) {
          if (mobileContainer) mobileContainer.innerHTML = '';
          if (desktopContainer) desktopContainer.innerHTML = '';
          return;
        }

        const html = tags.map(tag => {
          const safeTag = tag.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          const tagId = `${category}-tag-${safeTag}`;
          return `
            <div class="checkbox-control">
              <input type="checkbox" class="switch" id="${tagId}" value="${escHtml(tag)}" data-category="${category}" data-tag="${escHtml(tag)}">
              <label for="${tagId}" class="text-subdued">${escHtml(tag)}</label>
            </div>
          `;
        }).join('');

        if (mobileContainer) mobileContainer.innerHTML = html;
        if (desktopContainer) desktopContainer.innerHTML = html;

        this._bindTagFilterEvents(category);
      });
    }

    _bindTagFilterEvents(category) {
      const stateKey = category === 'interior' ? 'interiorSelectedTags' : 'accessorySelectedTags';
      const containerIds = [`#${category}-tag-filters-mobile`, `#${category}-tag-filters-desktop`];

      containerIds.forEach(selector => {
        const container = this.querySelector(selector);
        if (!container) return;

        container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          const newCb = cb.cloneNode(true);
          cb.replaceWith(newCb);
        });

        container.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
          checkbox.addEventListener('change', () => {
            const tag = checkbox.dataset.tag;
            let selected = [...this.state[stateKey]];

            if (checkbox.checked) {
              if (!selected.includes(tag)) selected.push(tag);
            } else {
              selected = selected.filter(t => t !== tag);
            }

            this.setState({ [stateKey]: selected });
            this.scheduleProductRender();
          });
        });
      });
    }

    // ─── Handlers ──────────────────────────────────────────────────────────────

    handleStepClick(stepEl) {
      const step = parseInt(stepEl.dataset.step);
      if (step === this.state.currentStep) return;
      if (step === 2 && !this.state.selectedCover) return;
      if (step === 3 && this.state.interiors.length === 0) return;
      this.setState({ currentStep: step });
      this.updateUI();
    }

    handleTypeSelect(el) {
      const newType = el.dataset.filterType;
      if (newType === this.state.coverType) return;

      this.setState({ coverType: newType, selectedColor: null, selectedCover: null, interiorSelectedTags: [], accessorySelectedTags: [] });
      el.parentElement.querySelector('.is-active')?.classList.remove('is-active');
      el.classList.add('is-active');

      this.updateCoverTypeButtons();
      this.renderCoverColorOptions();
      this.renderTagFilters();
      this._updatePriceFilterRange();
      this.renderProducts('interior', 'interior-products-grid');
      this.renderProducts('accessory', 'accessory-products-grid');
      this.updateCounts();
      this.updateRingOptions();

      const covers = this.getCoversByType(newType);
      if (covers.length > 0) {
        this.selectCover(covers[0].colorHex);
      } else {
        this.clearCoverDisplay();
      }
    }

    clearCoverDisplay() {
      ['#cover-gallery-carousel', '#cover-gallery-thumbnails', '#cover-gallery-dots', '#cover-price-hidden']
        .forEach(id => {
          const el = this.querySelector(id);
          if (el) el.innerHTML = '';
        });

      const titleEl = this.querySelector('#cover-product-title');
      if (titleEl) titleEl.textContent = '';

      const priceEl = this.querySelector('#cover-product-price');
      if (priceEl) priceEl.textContent = '\u2014';

      const priceListEl = this.querySelector('#cover-price-list');
      if (priceListEl) priceListEl.hidden = true;
    }

    handleColorSelect(el) {
      const colorHex = el.dataset.color;
      if (!colorHex || colorHex === this.state.selectedColor) return;
      this.selectCover(colorHex);
    }

    updateCoverTypeButtons() {
      this.querySelectorAll('.block-swatch[data-filter-type]').forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.filterType === this.state.coverType);
      });
    }

    handleRingSelect(el) {
      const ring = el.dataset.ring;
      if (!ring || ring === this.state.ring) return;

      const cover = this.state.selectedCover;
      const availableRings = this.getAvailableRings(cover);
      if (!availableRings.includes(ring)) return;

      this.setState({ ring });

      el.closest('.h-stack')?.querySelectorAll('[data-ring]').forEach(s => {
        const active = s.dataset.ring === ring;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-checked', active ? 'true' : 'false');
      });

      this._previewRingInGallery(ring);
      this.updateEngravingSummary();
      this.updateSummary();
    }

    handleStepNext(fromStep) {
      if (fromStep === 1) {
        if (!this.state.selectedCover) {
          const container = this.querySelector('#color-options');
          if (container) {
            container.style.outline = '2px solid var(--color-primary)';
            setTimeout(() => container.style.outline = '', 1200);
          }
          return;
        }
        this.setState({ currentStep: 2 });
      } else if (fromStep === 2) {
        this.setState({ currentStep: 3 });
      }
      this.updateUI();
    }

    handleNavBack(el) {
      this.closeProductDetailModal();
      const step = parseInt(el.dataset.step);
      if (el.classList.contains('is-disabled')) return;
      this.setState({ currentStep: step });
      this.updateUI();
    }

    openEngravingModal() {
      const modal = document.querySelector('#engraving-modal');
      const input = document.querySelector('#engraving-input');
      const mobile = document.querySelector('#engraving-input-mobile');
      const canvas = document.querySelector('#engraving-canvas');
      if (!modal) return;

      const engravingVal = this.state.engraving || '';
      if (input) input.value = engravingVal;
      if (mobile) mobile.value = engravingVal;

      if (typeof modal.show === 'function') {
        modal.show();
      } else {
        modal.setAttribute('open', '');
      }

      this.syncEngravingFontButtons();
      this.syncEngravingColorButtons();

      if (this._engravingResizeObserver) {
        this._engravingResizeObserver.disconnect();
      }
      this._engravingResizeObserver = null;

      const ro = new ResizeObserver(() => {
        if (this._engravingResizeObserver === ro) {
          this.drawEngravingPreview(this.state.engraving || '');
        }
      });
      ro.observe(modal);
      this._engravingResizeObserver = ro;

      setTimeout(() => {
        this.drawEngravingPreview(this.state.engraving || '');
      }, 80);
    }

    handleEngravingClose(action) {
      const modal = document.querySelector('#engraving-modal');
      const input = document.querySelector('#engraving-input');
      const mobile = document.querySelector('#engraving-input-mobile');

      if (action === 'engraving-confirm') {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const src = isMobile ? mobile : input;
        if (src) {
          this.state.engraving = this.cleanEngravingText(src.value);
        }
      }

      if (modal) {
        if (this._engravingResizeObserver) {
          this._engravingResizeObserver.disconnect();
          this._engravingResizeObserver = null;
        }
        if (typeof modal.hide === 'function') {
          modal.hide();
        }
        modal.removeAttribute('open');
        if (input) input.value = '';
        this.drawEngravingPreview('');
      }

      this.updateEngravingSummary();
    }

    updateEngravingSummary() {
      const summaryEl = document.querySelector('#engraving-summary');
      if (!summaryEl) return;

      const textLabel = t('engraving_summary_text_label', 'Text');
      const colorLabel = t('engraving_summary_color_label', 'Finish');
      const fontLabel = t('engraving_summary_font_label', 'Font');
      const ringLabel = t('engraving_summary_ring_label', 'Ring');
      const placeholder = t('engraving_summary_placeholder', 'Add your initials to make it truly yours!');

      const ring = this.state.ring === 'gold'
        ? t('ring_gold', 'Gold')
        : t('ring_silver', 'Silver');

      const ringRow = `
        <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
          <span style="color: var(--text-color-subdued, #666);">${ringLabel}:</span>
          <span>${ring}</span>
        </div>
      `;

      if (this.state.engraving && this.state.engraving.trim() !== '') {
        const engravingColorLabel = this._getEngravingFinishLabel();
        const engravingFont = this.state.engravingFont || 'DM Sans';
        const engravingText = this.state.engraving;

        summaryEl.innerHTML = `
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <span style="color: var(--text-color-subdued, #666);">${textLabel}:</span>
            <strong style="letter-spacing: 1px;">${engravingText}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <span style="color: var(--text-color-subdued, #666);">${colorLabel}:</span>
            <span>${engravingColorLabel}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <span style="color: var(--text-color-subdued, #666);">${fontLabel}:</span>
            <span>${engravingFont}</span>
          </div>
          ${ringRow}
        `;
        summaryEl.style.display = 'block';
      } else {
        summaryEl.innerHTML = `
          <p style="color: var(--text-color-subdued, #666); margin: 0 0 6px 0;">${placeholder}</p>
          ${ringRow}
        `;
        summaryEl.style.display = 'block';
      }
    }

    _syncEngravingFontStateFromDOM() {
      let active = document.querySelector('.engraving-font-options .font-swatch.is-active');
      if (!active) {
        active = document.querySelector('.engraving-font-options .font-swatch');
        if (active) active.classList.add('is-active');
      }
      if (active && active.dataset.fontFamily) {
        this.state.engravingFont = active.dataset.fontLabel;
        this.state.engravingFontFamily = active.dataset.fontFamily;
        this.state.engravingFontWeight = parseInt(active.style.getPropertyValue('--engraving-font-weight') || '500', 10);
      }
    }

    _getEngravingLayout(canvas) {
      const scaleRatio = canvas.width / 600;

      return {
        x: canvas.width * 0.5,
        y: canvas.height * 0.83,
        fontSize: Math.round(24 * scaleRatio), // Slightly bumped up to match reference
        rotation: 0,
        // Increased tracking to match the airy, elegant look of the physical stamp
        letterSpacing: Math.max(1, Math.round(3 * scaleRatio)),
        scaleY: 1 // STRICTLY 1: Removes the vertical squish so the font retains its true geometry
      };
    }

    _measureTransformedText(ctx, text, fontSize, letterSpacing) {
      let width = 0;
      const chars = Array.from(text);

      for (const char of chars) {
        width += ctx.measureText(char).width;
      }

      return width + letterSpacing * Math.max(0, chars.length - 1);
    }

    _drawTransformedText(ctx, text, layout, draw) {
      let width = 0;
      const chars = Array.from(text);

      for (const char of chars) {
        width += ctx.measureText(char).width;
      }

      width += layout.letterSpacing * Math.max(0, chars.length - 1);

      let cursorX = -width / 2;

      ctx.save();
      ctx.translate(layout.x, layout.y);
      ctx.rotate(layout.rotation);
      ctx.scale(1, layout.scaleY);

      for (const char of chars) {
        const charWidth = ctx.measureText(char).width;
        draw(ctx, char, cursorX + charWidth / 2, 0);
        cursorX += charWidth + layout.letterSpacing;
      }

      ctx.restore();
    }

    _drawFoilText(ctx, text, isSilver, layout) {
      // FORCE INTEGER OFFSET
      const dpr = window.devicePixelRatio || 1;
      const offset = Math.max(1, Math.round((layout.fontSize * 0.02) * dpr));

      // 1. THE METALLIC BASE GRADIENT
      // Using tighter color stops creates the sharp reflection typical of shiny metal
      const goldGradient = ctx.createLinearGradient(
        layout.x - layout.fontSize * 1.5, layout.y - layout.fontSize,
        layout.x + layout.fontSize * 1.5, layout.y + layout.fontSize
      );
      goldGradient.addColorStop(0, '#9A7B30');    // Dark gold edge
      goldGradient.addColorStop(0.2, '#D4AF37');  // Base gold
      goldGradient.addColorStop(0.4, '#FFF7B0');  // Bright reflection
      goldGradient.addColorStop(0.45, '#FFFFFF'); // The "Hotspot" (pure white glare)
      goldGradient.addColorStop(0.5, '#E8C354');  // Drop back to gold
      goldGradient.addColorStop(0.8, '#D4AF37');  // Base
      goldGradient.addColorStop(1, '#8C6714');    // Dark edge

      const silverGradient = ctx.createLinearGradient(
        layout.x - layout.fontSize * 1.5, layout.y - layout.fontSize,
        layout.x + layout.fontSize * 1.5, layout.y + layout.fontSize
      );
      silverGradient.addColorStop(0, '#7A7A7A');
      silverGradient.addColorStop(0.2, '#BDBDBD');
      silverGradient.addColorStop(0.4, '#F5F5F5');
      silverGradient.addColorStop(0.45, '#FFFFFF'); // The "Hotspot"
      silverGradient.addColorStop(0.5, '#D6D6D6');
      silverGradient.addColorStop(0.8, '#BDBDBD');
      silverGradient.addColorStop(1, '#666666');

      const foilFill = isSilver ? silverGradient : goldGradient;

      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;

      // Layer 1: Deboss Bottom/Right Highlight (Light catching the pressed leather)
      this._drawTransformedText(ctx, text, layout, (textCtx, char, charX, charY) => {
        textCtx.fillStyle = 'rgba(255, 255, 255, 0.65)';
        textCtx.fillText(char, charX, charY + offset);
      });

      // Layer 2: Deboss Top/Left Shadow (The physical shadow cast by the dent)
      this._drawTransformedText(ctx, text, layout, (textCtx, char, charX, charY) => {
        textCtx.fillStyle = 'rgba(0, 0, 0, 0.45)';
        textCtx.fillText(char, charX, charY - offset);
      });

      // Layer 3: Main Foil Base
      this._drawTransformedText(ctx, text, layout, (textCtx, char, charX, charY) => {
        textCtx.fillStyle = foilFill;
        textCtx.fillText(char, charX, charY);
      });

      // Layer 4: The "Shine" Overlay 
      // A vertical gradient that creates a glossy curve over the top half of the letters
      const shineGradient = ctx.createLinearGradient(0, layout.y - layout.fontSize / 2, 0, layout.y + layout.fontSize / 2);
      shineGradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
      shineGradient.addColorStop(0.3, 'rgba(255, 255, 255, 0)');
      shineGradient.addColorStop(0.7, 'rgba(0, 0, 0, 0)');
      shineGradient.addColorStop(1, isSilver ? 'rgba(0, 0, 0, 0.2)' : 'rgba(100, 50, 0, 0.2)');

      this._drawTransformedText(ctx, text, layout, (textCtx, char, charX, charY) => {
        textCtx.globalCompositeOperation = 'overlay';
        textCtx.fillStyle = shineGradient;
        textCtx.fillText(char, charX, charY);
      });

      // Layer 5: Subtle metallic rim/bevel
      // Real foil rounds off at the sharp edges of the stamp. This catches that micro-edge light.
      this._drawTransformedText(ctx, text, layout, (textCtx, char, charX, charY) => {
        textCtx.globalCompositeOperation = 'source-over';
        textCtx.lineWidth = Math.max(0.5, layout.fontSize * 0.015);
        textCtx.strokeStyle = isSilver ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 245, 180, 0.6)';
        textCtx.strokeText(char, charX, charY);
      });

      ctx.globalCompositeOperation = 'source-over'; // Reset
    }

    _drawBlindText(ctx, text, layout) {
      // FORCE INTEGER OFFSET
      const dpr = window.devicePixelRatio || 1;
      const offset = Math.max(1, Math.round((layout.fontSize * 0.02) * dpr));

      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;

      // Layer 1: Bottom/Right Highlight
      this._drawTransformedText(ctx, text, layout, (textCtx, char, charX, charY) => {
        textCtx.fillStyle = 'rgba(255, 255, 255, 0.45)';
        textCtx.fillText(char, charX, charY + offset);
      });

      // Layer 2: Top/Left Shadow
      this._drawTransformedText(ctx, text, layout, (textCtx, char, charX, charY) => {
        textCtx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        textCtx.fillText(char, charX, charY - offset);
      });

      // Layer 3: Deepen the leather color directly in the center via multiply
      this._drawTransformedText(ctx, text, layout, (textCtx, char, charX, charY) => {
        textCtx.globalCompositeOperation = 'multiply';
        textCtx.fillStyle = 'rgba(0, 0, 0, 0.15)';
        textCtx.fillText(char, charX, charY);
      });

      ctx.globalCompositeOperation = 'source-over'; // Reset
    }

    // Extracted drawing logic to reuse the cached image
    _renderCanvasText(ctx, canvas, img, text) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const ratio = Math.max(
        canvas.width / img.width,
        canvas.height / img.height
      );
      const w = img.width * ratio;
      const h = img.height * ratio;
      const x = (canvas.width - w) / 2;
      const y = (canvas.height - h) / 2;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, x, y, w, h);

      if (text && text.trim()) {
        const engravingText = String(text).trim().toUpperCase();
        const fontFamily = this.state.engravingFontFamily || "'DM Sans', sans-serif";
        const fontWeight = this.state.engravingFontWeight || 500;
        const layout = this._getEngravingLayout(canvas);
        const maxWidth = canvas.width * 0.78;
        let fontSize = layout.fontSize;

        ctx.textRenderingOptimization = 'optimizeLegibility';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

        while (this._measureTransformedText(ctx, engravingText, fontSize, layout.letterSpacing) > maxWidth && fontSize > 12) {
          fontSize -= 1;
          layout.fontSize = fontSize;
          ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        }

        if (isBlindEngravingColor(this.state.engravingColor)) {
          this._drawBlindText(ctx, engravingText, layout);
        } else {
          this._drawFoilText(ctx, engravingText, isSilverEngravingColor(this.state.engravingColor), layout);
        }
      }
    }

    drawEngravingPreview(text) {
      const canvas = document.querySelector('#engraving-canvas');
      const cover = this.state.selectedCover;
      if (!canvas || !cover) return;

      this._prepareCanvasSize(canvas);

      const src = (cover.images && cover.images[0]) || cover.image || cover.secondaryImage || '';
      if (!src) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      const loadImage = () => new Promise((resolve, reject) => {
        if (this._cachedEngravingSrc === src && this._cachedEngravingImage) {
          resolve(this._cachedEngravingImage);
          return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          this._cachedEngravingImage = img;
          this._cachedEngravingSrc = src;
          resolve(img);
        };
        img.onerror = reject;
        img.src = src;
      });

      const requestId = ++this._engravingRenderId;

      loadImage()
        .then(img => {
          if (requestId !== this._engravingRenderId) return img;
          const fontFamily = this.state.engravingFontFamily || "'DM Sans', sans-serif";
          const fontWeight = this.state.engravingFontWeight || 500;
          let fontResolve;
          const fontPromise = new Promise(resolve => { fontResolve = resolve; });
          try {
            const loaded = document.fonts.load(`${fontWeight} 12px ${fontFamily}`);
            if (loaded && typeof loaded.then === 'function') {
              loaded.then(fontResolve).catch(fontResolve);
            } else {
              fontResolve();
            }
          } catch (_) {
            fontResolve();
          }
          return Promise.all([fontPromise, document.fonts.ready]).then(() => img);
        })
        .then(img => {
          if (requestId !== this._engravingRenderId) return;
          this._renderCanvasText(canvas.getContext('2d'), canvas, img, text);
        })
        .catch(() => {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        });
    }

    _prepareCanvasSize(canvas) {
      const wrap = canvas.closest('.engraving-canvas-wrap') || canvas.parentElement;
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const targetW = Math.max(1, Math.round(rect.width * dpr));
      const targetH = Math.max(1, Math.round(rect.height * dpr));
      canvas.width = targetW;
      canvas.height = targetH;
    }

    handleProductAdd(card) {
      const category = card.dataset.category;
      const productId = parseInt(card.dataset.productId);
      if (isNaN(productId)) {
        const handle = card.dataset.handle;
        if (!handle) return;
        this._setProductQuantityByHandle(category, handle, 1);
        return;
      }
      if (category === 'interior') {
        this._setProductQuantity('interior', productId, 1);
      } else if (category === 'accessory') {
        this._setProductQuantity('accessory', productId, 1);
      }
    }

    handleProductInc(card) {
      const category = card.dataset.category;
      const productId = parseInt(card.dataset.productId);
      if (isNaN(productId)) {
        const handle = card.dataset.handle;
        if (!handle) return;
        const currentQty = this._getProductQuantityByHandle(handle);
        const newQty = currentQty <= 0 ? 1 : currentQty + 1;
        this._setProductQuantityByHandle(category, handle, newQty);
        return;
      }
      const currentQty = this._getProductQuantity(productId);
      const newQty = currentQty <= 0 ? 1 : currentQty + 1;
      if (category === 'interior') {
        this._setProductQuantity('interior', productId, newQty);
      } else if (category === 'accessory') {
        this._setProductQuantity('accessory', productId, newQty);
      }
    }

    handleProductDec(card) {
      const category = card.dataset.category;
      const productId = parseInt(card.dataset.productId);
      if (isNaN(productId)) {
        const handle = card.dataset.handle;
        if (!handle) return;
        const currentQty = this._getProductQuantityByHandle(handle);
        if (currentQty <= 1) {
          this._removeProductByHandle(category, handle);
        } else {
          this._setProductQuantityByHandle(category, handle, currentQty - 1);
        }
        return;
      }
      const currentQty = this._getProductQuantity(productId);
      if (currentQty <= 1) {
        if (category === 'interior') {
          this._removeProduct('interior', productId);
        } else if (category === 'accessory') {
          this._removeProduct('accessory', productId);
        }
      } else {
        if (category === 'interior') {
          this._setProductQuantity('interior', productId, currentQty - 1);
        } else if (category === 'accessory') {
          this._setProductQuantity('accessory', productId, currentQty - 1);
        }
      }
    }

    handleProductCardClick(card) {
      const productId = parseInt(card.dataset.productId);
      const handle = card.dataset.handle;
      const category = card.dataset.category;

      let product;
      if (!isNaN(productId)) {
        product = this.products.find(p => p.id === productId);
      }
      if (!product) {
        product = this.products.find(p => p.handle === handle);
      }
      if (!product || !product.available) return;

      this._modalProduct = product;
      this._modalCategory = category;
      this._populateProductModal(product);

      const modal = document.querySelector('#product-detail-modal');
      if (modal) {
        if (typeof modal.show === 'function') modal.show();
        else modal.setAttribute('open', '');
      }
    }

    _populateProductModal(product) {
      const titleEl = document.querySelector('#modal-product-title');
      if (titleEl) titleEl.textContent = product.title;

      const priceEl = document.querySelector('#modal-product-price');
      if (priceEl) priceEl.textContent = product.priceFormatted;

      this._populateModalGallery(product);

      const descEl = document.querySelector('#modal-description-content');
      if (descEl) descEl.innerHTML = product.description || '';

      const includesEl = document.querySelector('#modal-includes-content');
      if (includesEl) includesEl.innerHTML = (product.bundleInclude || '').replace(/\n/g, '<br>');

      const includesEl2 = document.querySelector('#modal-includes-content2');
      if (includesEl2) includesEl2.innerHTML = (product.bundleInclude || '').replace(/\n/g, '<br>');

      const modalRating = document.querySelector('#modal-rating');
      if (modalRating) {
        if (product.judgemeBadge) {
          modalRating.innerHTML = product.judgemeBadge;
          modalRating.hidden = false;
        } else {
          modalRating.innerHTML = '';
          modalRating.hidden = true;
        }
      }

      const qty = this._getModalQuantity() || 1;
      this._modalLocalQty = qty;
      const qtyValueEl = document.querySelector('#modal-qty-value');
      if (qtyValueEl) qtyValueEl.textContent = qty;

      this.updateProductReviews(product.handle, '#modal-reviews-container');
    }

    _populateModalGallery(product) {
      const imagesContainer = document.querySelector('#modal-gallery-carousel');
      const thumbsContainer = document.querySelector('#modal-gallery-thumbnails');
      const dotsContainer = document.querySelector('#modal-gallery-dots-thumbs');

      if (!imagesContainer || !thumbsContainer) return;

      const allImages = ((product.images && product.images.length) ? product.images : [product.image, product.secondaryImage].filter(Boolean)).slice(0, 4);

      if (dotsContainer) {
        dotsContainer.innerHTML = allImages.map((_, i) => {
          const isCurrent = i === 0 ? 'true' : 'false';
          return `
            <button type="button" class="product-gallery__thumbnail gallery-dot" aria-current="${isCurrent}" data-media-position="${i + 1}"></button>`;
        }).join('');

        dotsContainer.querySelectorAll('.gallery-dot').forEach((dot) => {
          dot.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleDotClick(dot);
          });
        });
      }

      thumbsContainer.innerHTML = allImages.map((src, i) => {
        const isCurrent = i === 0 ? 'true' : 'false';
        return `
          <button type="button" class="product-gallery__thumbnail" aria-current="${isCurrent}"
                  data-media-position="${i + 1}" data-media-id="gallery-${i}" aria-label="Ir a imagen ${i + 1}">
            <img src="${escHtml(src)}" alt="${escHtml(product.title)} - Thumbnail ${i + 1}" width="56" height="56" sizes="56px" loading="lazy" class="object-contain">
          </button>`;
      }).join('');

      this._savedGallery = {
        activeGallery: this._activeGallery,
        thumbnailList: this._coverThumbnailList,
        dragScroller: this._galleryDrag.scroller
      };

      this._activeGallery = { imagesContainer, images: allImages };
      this._coverThumbnailList = thumbsContainer.parentElement;
      this._galleryDrag.scroller = thumbsContainer;
      this._galleryDrag.suppressClickUntil = 0;

      this._bindGalleryInteractions(thumbsContainer);
      this._replaceGalleryImage(imagesContainer, allImages, 0);
      this._bindCarouselSwipe(imagesContainer);
    }

    _restoreCoverGallery() {
      if (this._coverThumbnailList) {
        this._coverThumbnailList.removeEventListener('click', this._handleGalleryClick, true);
        this._coverThumbnailList.removeEventListener('mousedown', this._handleGalleryMouseDown);
        this._coverThumbnailList.removeEventListener('touchstart', this._handleGalleryTouchStart);
        this._coverThumbnailList.removeEventListener('touchmove', this._handleGalleryTouchMove);
        this._coverThumbnailList.removeEventListener('touchend', this._handleGalleryTouchEnd);
        this._coverThumbnailList.removeEventListener('touchcancel', this._handleGalleryTouchEnd);
        this._coverThumbnailList = null;
      }

      if (this._savedGallery) {
        this._activeGallery = this._savedGallery.activeGallery;
        this._coverThumbnailList = this._savedGallery.thumbnailList;
        this._galleryDrag.scroller = this._savedGallery.dragScroller;
      }

      const coverThumbs = this.querySelector('#cover-gallery-thumbnails');
      if (coverThumbs && coverThumbs.parentElement) {
        this._bindGalleryInteractions(coverThumbs);
      }
    }

    _handleModalInc() {
      if (!this._modalProduct) return;
      this._modalLocalQty = Math.max(1, (this._modalLocalQty || 1) + 1);
      const el = document.querySelector('#modal-qty-value');
      if (el) el.textContent = this._modalLocalQty;
    }

    _handleModalDec() {
      if (!this._modalProduct) return;
      this._modalLocalQty = Math.max(1, (this._modalLocalQty || 1) - 1);
      const el = document.querySelector('#modal-qty-value');
      if (el) el.textContent = this._modalLocalQty;
    }

    _getModalQuantity() {
      if (!this._modalProduct) return 0;
      const product = this._modalProduct;
      if (product.id != null && !isNaN(product.id)) {
        return this._getProductQuantity(product.id);
      }
      return this._getProductQuantityByHandle(product.handle);
    }

    _setModalQuantity(quantity) {
      if (!this._modalProduct) return;
      const product = this._modalProduct;
      const category = this._modalCategory;

      if (product.id != null && !isNaN(product.id)) {
        this._setProductQuantity(category, product.id, quantity);
      } else {
        this._setProductQuantityByHandle(category, product.handle, quantity);
      }

      const qtyValueEl = document.querySelector('#modal-qty-value');
      if (qtyValueEl) qtyValueEl.textContent = Math.max(1, quantity);
    }

    _removeModalProduct() {
      if (!this._modalProduct) return;
      const product = this._modalProduct;
      const category = this._modalCategory;

      if (product.id != null && !isNaN(product.id)) {
        this._removeProduct(category, product.id);
      } else {
        this._removeProductByHandle(category, product.handle);
      }
    }

    closeProductDetailModal() {
      this._restoreCoverGallery();
      this._modalProduct = null;
      this._modalCategory = null;
      this._modalLocalQty = 1;

      const modal = document.querySelector('#product-detail-modal');
      if (modal) {
        if (typeof modal.hide === 'function') modal.hide();
        modal.removeAttribute('open');
      }
    }

    _toggleFavorite() {
      const btn = document.querySelector('#modal-favorite-btn');
      if (!btn) return;
      btn.classList.toggle('is-favorited');
    }

    // Build the object stored in state.interiors / state.accessories. It must
    // carry the full product data (title + variants) because several consumers
    // (validation, the "Includes" summary) read those fields directly.
    _normalizeStoredProduct(product, quantity) {
      if (!product) return null;
      return {
        id: product.id != null ? product.id : (product.productId != null ? product.productId : null),
        handle: product.handle || '',
        quantity: quantity || 1,
        price: product.price || 0,
        title: product.title || product.handle || null,
        variantId: product.variantId != null ? product.variantId : null,
        variants: Array.isArray(product.variants) ? product.variants : [],
        available: !!product.available
      };
    }

    _setProductQuantity(category, productId, quantity) {
      const arr = category === 'interior' ? this.state.interiors : this.state.accessories;
      const product = this.products.find(p => p.id === productId);

      if (quantity <= 0) {
        const idx = arr.findIndex(p => p.id === productId);
        if (idx > -1) arr.splice(idx, 1);
      } else {
        const existing = arr.find(p => p.id === productId);
        if (existing) {
          existing.quantity = quantity;
        } else {
          const normalized = this._normalizeStoredProduct(product, quantity);
          if (normalized) arr.push(normalized);
        }
      }

      this.state.quantities[productId] = Math.max(0, quantity);
      this.updateUI();
      this._updateCardButton(product ? product.id : null, product ? product.handle : null);
    }

    _removeProduct(category, productId) {
      const arr = category === 'interior' ? this.state.interiors : this.state.accessories;
      const idx = arr.findIndex(p => p.id === productId);
      if (idx > -1) arr.splice(idx, 1);
      const product = this.products.find(p => p.id === productId);
      this.state.quantities[productId] = 0;
      this.updateUI();
      this._updateCardButton(product ? product.id : null, product ? product.handle : null);
    }

    _updateCardButton(productId, handle) {
      let card = productId != null && !isNaN(productId)
        ? this.querySelector(`.bundle-product-card[data-product-id="${productId}"]`)
        : null;
      if (!card && handle) {
        card = this.querySelector(`.bundle-product-card[data-handle="${handle}"]`);
      }
      if (!card) return;
      const product = productId != null && !isNaN(productId)
        ? this.products.find(p => p.id === productId)
        : this.products.find(p => p.handle === handle);
      if (!product) return;
      const oldControl = card.querySelector('.product-card__quick-add-button, .bundle-product-card__qty-control');
      if (!oldControl) return;
      oldControl.outerHTML = this._getProductCardButton(product);
    }

    _getProductCardButton(product) {
      const hasNumericId = product.id != null && !isNaN(product.id);
      const qty = hasNumericId
        ? this._getProductQuantity(product.id)
        : this._getProductQuantityByHandle(product.handle);
      const inCart = qty > 0;

      if (!product.available) {
        if (inCart) {
          return `
            <div class="bundle-product-card__qty-control" role="group" aria-label="Cantidad para ${escHtml(product.title)}">
              <button type="button" class="bundle-product-card__qty-dec" aria-label="Reducir cantidad">
                <svg aria-hidden="true" focusable="false" fill="none" width="12" viewBox="0 0 12 12">
                  <path d="M0 6h12" stroke="currentColor" stroke-width="1.5"></path>
                </svg>
              </button>
              <span class="bundle-product-card__qty-value" aria-live="polite">${qty}</span>
              <button type="button" class="bundle-product-card__qty-inc" aria-label="Aumentar cantidad" disabled>
                <svg aria-hidden="true" focusable="false" fill="none" width="12" class="icon icon-plus" viewBox="0 0 12 12">
                  <path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1.5"></path>
                </svg>
              </button>
            </div>`;
        }
        return '';
      }

      if (qty > 0) {
        return `
          <div class="bundle-product-card__qty-control" role="group" aria-label="Cantidad para ${escHtml(product.title)}">
            <button type="button" class="bundle-product-card__qty-dec" aria-label="Reducir cantidad">
              <svg aria-hidden="true" focusable="false" fill="none" width="12" viewBox="0 0 12 12">
                <path d="M0 6h12" stroke="currentColor" stroke-width="1.5"></path>
              </svg>
            </button>
            <span class="bundle-product-card__qty-value" aria-live="polite">${qty}</span>
            <button type="button" class="bundle-product-card__qty-inc" aria-label="Aumentar cantidad">
              <svg aria-hidden="true" focusable="false" fill="none" width="12" class="icon icon-plus" viewBox="0 0 12 12">
                <path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1.5"></path>
              </svg>
            </button>
          </div>`;
      }
      return `
        <button type="button" aria-controls="product-quick-buy-${escHtml(product.id)}" class="product-card__quick-add-button bundle-product-card__add-btn">
          <svg aria-hidden="true" focusable="false" fill="none" width="12" class="icon icon-plus" viewBox="0 0 12 12">
            <path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1.5"></path>
          </svg>
        </button>`;
    }

    _getProductQuantity(productId) {
      const interior = this.state.interiors.find(p => p.id === productId);
      const accessory = this.state.accessories.find(p => p.id === productId);
      return interior ? interior.quantity : (accessory ? accessory.quantity : 0);
    }

    _getProductQuantityByHandle(handle) {
      const interior = this.state.interiors.find(p => p.handle === handle);
      const accessory = this.state.accessories.find(p => p.handle === handle);
      return interior ? interior.quantity : (accessory ? accessory.quantity : 0);
    }

    _setProductQuantityByHandle(category, handle, quantity) {
      const arr = category === 'interior' ? this.state.interiors : this.state.accessories;
      const product = this.products.find(p => p.handle === handle);

      if (quantity <= 0) {
        const idx = arr.findIndex(p => p.handle === handle);
        if (idx > -1) arr.splice(idx, 1);
      } else {
        const existing = arr.find(p => p.handle === handle);
        if (existing) {
          existing.quantity = quantity;
        } else {
          const normalized = this._normalizeStoredProduct(product, quantity);
          if (normalized) arr.push(normalized);
        }
      }

      const numericId = product ? product.id : null;
      this.state.quantities[numericId || handle] = Math.max(0, quantity);
      this.updateUI();
      this._updateCardButton(numericId, handle);
    }

    _removeProductByHandle(category, handle) {
      const arr = category === 'interior' ? this.state.interiors : this.state.accessories;
      const idx = arr.findIndex(p => p.handle === handle);
      if (idx > -1) arr.splice(idx, 1);
      const product = this.products.find(p => p.handle === handle);
      const numericId = product ? product.id : handle;
      this.state.quantities[numericId] = 0;
      this.updateUI();
      this._updateCardButton(product ? product.id : null, handle);
    }

    handleSortChange(option) {
      const sort = option.dataset.sort;
      if (!sort) return;

      if (this.state.currentStep === 2) {
        this._interiorSort = sort;
        this.renderProducts('interior', 'interior-products-grid');
      } else if (this.state.currentStep === 3) {
        this._accessorySort = sort;
        this.renderProducts('accessory', 'accessory-products-grid');
      }
    }

    // ─── Rendering ─────────────────────────────────────────────────────────────

    renderProducts(category, containerId) {
      const container = this.querySelector('#' + containerId);
      if (!container) return;

      const sortKey = category === 'interior' ? '_interiorSort' : '_accessorySort';
      const sort = this[sortKey] || 'title-ascending';

      let filtered = this.products.filter(p => p.category === category).filter(p => {
        if (!p.type) return true;
        return p.type === this.state.coverType;
      });

      const isInterior = category === 'interior';
      const inStockOnly = isInterior ? this.state.interiorInStockOnly : this.state.accessoryInStockOnly;
      const priceMin = isInterior ? this.state.interiorPriceMin : this.state.accessoryPriceMin;
      const priceMax = isInterior ? this.state.interiorPriceMax : this.state.accessoryPriceMax;

      if (inStockOnly) filtered = filtered.filter(p => p.available);
      if (priceMin != null && priceMin !== '') filtered = filtered.filter(p => (p.price || 0) >= Number(priceMin) * 100);
      if (priceMax != null && priceMax !== '') filtered = filtered.filter(p => (p.price || 0) <= Number(priceMax) * 100);

      const selectedTags = isInterior ? this.state.interiorSelectedTags : this.state.accessorySelectedTags;
      if (selectedTags.length > 0) {
        filtered = filtered.filter(p => {
          if (!p.tags || !Array.isArray(p.tags)) return false;
          return selectedTags.some(tag => p.tags.includes(tag));
        });
      }

      filtered.sort((a, b) => {
        switch (sort) {
          case 'price-ascending': return (a.price || 0) - (b.price || 0);
          case 'price-descending': return (b.price || 0) - (a.price || 0);
          case 'title-descending': return (b.title || '').localeCompare(a.title || '');
          case 'title-ascending':
          default: return (a.title || '').localeCompare(b.title || '');
        }
      });

      if (filtered.length === 0) {
        container.innerHTML = '<p class="text-subdued text-sm">No hay productos disponibles.</p>';
        return;
      }

      container.innerHTML = filtered.map((product) => {
        const productId = product.id;
        const productIdAttr = String(productId != null && !isNaN(productId) ? productId : `handle-${product.handle || Math.random().toString(36).slice(2, 9)}`);
        const secondaryImg = product.secondaryImage || product.image || '';
        const secondaryImgTag = secondaryImg ? `<img src="${escHtml(secondaryImg)}" alt="${escHtml(product.title)}" loading="lazy" class="product-card__image product-card__image--secondary" fetchpriority="low" draggable="false" aria-hidden="true">` : '';
        const soldOutBadge = !product.available ? `<span class="bundle-product-card__sold-out-badge">${escHtml(t('product.general.sold_out_badge', 'Sold out'))}</span>` : '';
        const cardClasses = ['product-card', 'bundle-product-card', !product.available ? 'is-sold-out' : ''].filter(Boolean).join(' ');

        return `
          <div class="product-card bundle-product-card" data-product-id="${escHtml(productIdAttr)}" data-handle="${escHtml(product.handle)}" data-category="${escHtml(category)}">
            <div class="product-card__figure">
              <a href="/products/${escHtml(product.handle)}" class="product-card__media" data-instant="" draggable="false">
                <img src="${escHtml(product.image)}" alt="${escHtml(product.title)}" loading="lazy" class="product-card__image product-card__image--primary aspect-natural" width="1200" height="1200" sizes="(max-width: 699px) calc(100vw / 2), (max-width: 999px) calc(100vw / 0 - 64px), calc((100vw - 96px) / 3 - (24px / 3 * 2))" draggable="false">
                ${secondaryImgTag}
                ${soldOutBadge}
              </a>
              ${this._getProductCardButton(product)}
            </div>
            <div class="product-card__info empty:hidden">
              <div class="v-stack justify-items-center gap-2">
                <div class="v-stack justify-items-center gap-1">
                  <a href="/products/${escHtml(product.handle)}" class="product-title h6 line-clamp">${escHtml(product.title)}</a>
                  <price-list class="price-list">
                    <sale-price class="h6 text-subdued">${escHtml(product.priceFormatted)}</sale-price>
                  </price-list>
                </div>
              </div>
            </div>
          </div>`;
      }).join('');
    }

    updateCounts() {
      const uiElements = {
        '.interior-products-count': p => p.category === 'interior',
        '.accessory-products-count': p => p.category === 'accessory'
      };

      for (const [selector, filterFn] of Object.entries(uiElements)) {
        const el = this.querySelector(selector);
        if (el) el.textContent = `${this.products.filter(filterFn).length} productos`;
      }

      const interiorActive = this.querySelector('.interior-active-count');
      const accessoryActive = this.querySelector('.accessory-active-count');

      if (interiorActive) interiorActive.textContent = this.state.interiors.length > 0 ? `(${this.state.interiors.length})` : '';
      if (accessoryActive) accessoryActive.textContent = this.state.accessories.length > 0 ? `(${this.state.accessories.length})` : '';
    }

    // ─── State & UI ────────────────────────────────────────────────────────────

    setState(newState) {
      Object.assign(this.state, newState);
    }

    updateProgress() {
      const progress = (this.state.currentStep - 1) * 50;
      const indicator = this.querySelector('.bundle-progress__indicator');
      if (indicator) indicator.style.width = `${progress}%`;

      const stepIndicator = this.querySelector('#step-indicator');
      if (stepIndicator) {
        const activeStepEl = this.querySelector('.stepper__item') || null;
        stepIndicator.textContent = activeStepEl ? activeStepEl.textContent.trim() : `Paso ${this.state.currentStep}`;
      }
    }

    updateUI() {
      this.querySelectorAll('.bundle-progress__step').forEach((step, idx) => {
        const isActive = (idx + 1) === this.state.currentStep;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-selected', isActive);
      });

      this.querySelectorAll('.bundle-step').forEach((step, idx) => {
        step.classList.toggle('is-active', (idx + 1) === this.state.currentStep);
      });

      this.querySelectorAll('.bundle-nav-back').forEach(btn => {
        btn.classList.toggle('is-disabled', parseInt(btn.dataset.step) >= this.state.currentStep);
      });

      const indicator = this.querySelector('#step-indicator');
      if (indicator) {
        const activeEl = this.querySelector('.stepper__step--active .stepper__label');
        indicator.textContent = activeEl ? activeEl.textContent.trim() : this.getStepTitle(this.state.currentStep);
      }

      this.querySelectorAll('.stepper__item').forEach((item, idx) => {
        item.classList.toggle('stepper__step--active', (idx + 1) === this.state.currentStep);
      });

      this.updateSummary();
      this.updateProgress();
      this.updateCoverTypeButtons();
      this.updateProductCardSelection();
      this.updateStickyCTA();
      this.updateCounts();
    }

    // Safely normalize a product key so numeric IDs and handle-string fallbacks
    // (e.g. "handle-cover-item") resolve to the same value used in DOM data attributes.
    _normalizeProductKey(rawId, handle = '') {
      if (typeof rawId === 'number' && !isNaN(rawId)) return rawId;
      if (typeof rawId === 'string' && rawId.trim() !== '') {
        const parsed = parseInt(rawId, 10);
        if (!isNaN(parsed)) return parsed;
        return rawId;
      }
      return handle || String(rawId || '');
    }

    updateProductCardSelection() {
      const selectedKeys = new Set([
        ...this.state.interiors.map(p => this._normalizeProductKey(p.id, p.handle)),
        ...this.state.accessories.map(p => this._normalizeProductKey(p.id, p.handle))
      ]);

      this.querySelectorAll('.bundle-product-card').forEach(card => {
        const key = this._normalizeProductKey(card.dataset.productId, card.dataset.handle);
        card.classList.toggle('is-selected', selectedKeys.has(key));
      });
    }

    updateStickyCTA() {
      const btn = this.querySelector('#bundle-sticky-cta');
      const backBtn = this.querySelector('#bundle-sticky-back-cta');
      if (!btn && !backBtn) return;

      const step = this.state.currentStep;


      if (backBtn) {
        backBtn.style.visibility = step === 1 ? 'hidden' : 'visible';
        backBtn.textContent = backBtn.getAttribute(`data-step${step}`) || '';
        backBtn.onclick = () => {
          this.closeProductDetailModal();
          if (step === 3) this.setState({ currentStep: 2 });
          else if (step === 2) this.setState({ currentStep: 1 });
          this.updateUI();
        };
      }

      if (btn) {
        // Guarantee the CTA is interactive. handleCheckout() disables it while
        // adding to cart and only the error path used to re-enable it, so a
        // successful add could leave it permanently disabled (a disabled button
        // swallows all clicks). Clear it here as a safety net for any path that
        // ends without going through the normal re-enable (e.g. the post-checkout
        // modal dismissed via Esc / outside click).
        btn.disabled = false;
        btn.removeAttribute('aria-busy');

        let text = '';
        if (step === 1) {
          text = btn.getAttribute('data-step1') || '';
        } else if (step === 2) {
          const hasInterior = this.state.interiors.length > 0;
          text = hasInterior
            ? (btn.getAttribute('data-step2') || '')
            : (t('continue_without_interiors', '') + ' >');
        } else if (step === 3) {
          const hasAccessory = this.state.accessories.length > 0;
          text = hasAccessory
            ? (btn.getAttribute('data-step3') || '')
            : (t('continue_without_accessories', '') + ' >');
        }
        btn.textContent = text;
        if (step === 1) btn.onclick = () => this.handleStepNext(1);
        else if (step === 2) btn.onclick = () => this.handleStepNext(2);
        else if (step === 3) btn.onclick = () => this.handleCheckout();
      }
    }


    updateSummary() {
      const coverPrice = this.state.selectedCover ? this.state.selectedCover.price : 0;
      const totalCount = (this.state.selectedCover ? 1 : 0) +
        this.state.interiors.reduce((sum, p) => sum + (p.quantity || 1), 0) +
        this.state.accessories.reduce((sum, p) => sum + (p.quantity || 1), 0);
      const totalPrice = coverPrice +
        this.state.interiors.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0) +
        this.state.accessories.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0);

      const countEl = this.querySelector('#bundle-total-count');
      const priceEl = this.querySelector('#bundle-total-price');

      if (countEl) countEl.textContent = totalCount;
      if (priceEl) priceEl.textContent = this._currencyFormatter.format(totalPrice / 100);

      const coverTypeEl = this.querySelector('#bundle-summary-cover-type');
      const selectedColorEl = this.querySelector('#bundle-summary-selected-color');
      const ringEl = this.querySelector('#bundle-summary-ring');

      const coverType = this.state.coverType === 'mini'
        ? t('cover_type_mini', 'Mini')
        : t('cover_type_original', 'Original');
      const selectedColor = this.state.selectedCover?.title || '—';
      const ring = this.state.ring === 'gold'
        ? t('ring_gold', 'Gold')
        : t('ring_silver', 'Silver');

      if (coverTypeEl) coverTypeEl.textContent = coverType;
      if (selectedColorEl) selectedColorEl.textContent = selectedColor;
      if (ringEl) ringEl.textContent = ring;
    }

    _getFormattedBundleComponents() {
      const components = [];
      if (this.state.selectedCover) {
        components.push(`${t('cart_bundle_includes_cover', 'Cubierta')}: ${this.state.selectedCover.title}`);
      }

      if (this.state.interiors && this.state.interiors.length) {
        const interiors = this.state.interiors.map(i => `${i.quantity || 1}x ${i.title}`).join(', ');
        components.push(`${t('cart_bundle_includes_interiors', 'Interiores')}: ${interiors}`);
      }

      if (this.state.accessories && this.state.accessories.length) {
        const accessories = this.state.accessories.map(a => `${a.quantity || 1}x ${a.title}`).join(', ');
        components.push(`${t('cart_bundle_includes_accessories', 'Accesorios')}: ${accessories}`);
      }

      let fullString = components.join(' | ');

      if (fullString.length > 252) {
        fullString = fullString.substring(0, 249) + '...';
      }

      return fullString;
    }

    _getEngravingProperties() {
      if (!this.state.engraving || this.state.engraving.trim() === '') return {};

      const ec = String(this.state.engravingColor || '').toLowerCase();
      let colorLabel = this.state.engravingColor;
      if (isBlindEngravingColor(this.state.engravingColor)) {
        colorLabel = t('engraving_blind', 'Ciego') || 'Ciego';
      } else if (isSilverEngravingColor(this.state.engravingColor)) {
        colorLabel = t('engraving_color_silver', 'Plata') || 'Plata';
      } else if (ec === '#d4af37' || ec.includes('gold')) {
        colorLabel = t('engraving_color_gold', 'Oro') || 'Oro';
      }

      return {
        Engraving: this.state.engraving,
        Text: this.state.engraving,
        'Engraving Font': this.state.engravingFont || '',
        'Engraving Color': colorLabel,
        'Engraving Color Hex': this.state.engravingColor
      };
    }

    _getEngravingFinishLabel() {
      if (isBlindEngravingColor(this.state.engravingColor)) {
        return window.Shopify?.locale?.startsWith('es') ? 'Sin color' : 'No color';
      }

      if (isSilverEngravingColor(this.state.engravingColor)) {
        return window.Shopify?.locale?.startsWith('es') ? 'Plata' : 'Silver';
      }

      return window.Shopify?.locale?.startsWith('es') ? 'Oro' : 'Gold';
    }

    _getCurrencyFormatter() {
      if (this._cachedFormatter) return this._cachedFormatter;

      const locale = (window.Shopify && window.Shopify.locale) || 'es-MX';
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'MXN';

      try {
        this._cachedFormatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2
        });
      } catch (e) {
        console.warn('[BundleConfigurator] Intl.NumberFormat failed, using basic fallback', e);
        this._cachedFormatter = {
          format: (val) => `$${Number(val).toFixed(2)} ${currency}`
        };
      }

      return this._cachedFormatter;
    }

    // ─── Checkout ──────────────────────────────────────────────────────────────

    _resolveVariantId(item) {
      if (!item) return null;
      // Use an explicit variant id when present and distinct from the product id.
      if (item.variantId && item.variantId !== item.id && item.variantId !== item.productId) {
        return item.variantId;
      }
      // Otherwise resolve from the master catalog by product id or handle.
      const product = this.products.find(p =>
        String(p.id) === String(item.id) ||
        String(p.productId) === String(item.id) ||
        (item.handle && p.handle === item.handle)
      );
      if (!product) return null;
      return product.variantId || (product.variants && product.variants[0] ? product.variants[0].id : null);
    }

    _createErrorContainer() {
      let el = this.querySelector('#bundle-error-message');
      if (el) return el;
      el = document.createElement('div');
      el.id = 'bundle-error-message';
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-live', 'assertive');
      el.className = 'bundle-error-message hidden';
      const anchor = this.querySelector('#bundle-bottom-bar') || this;
      anchor.appendChild(el);
      return el;
    }

    _showBundleError(message) {
      const el = this._createErrorContainer();
      el.textContent = message;
      el.classList.remove('hidden');
    }

    // Returns an array of human-readable problems for any bundle item that is
    // unavailable or whose variant can no longer be resolved. Lets us fail fast
    // (with a clear message) instead of relying on a generic /cart/add.js 422.
    _validateBundleItems(selectedCover, coverVariantId) {
      const problems = [];

      const checkProduct = (stored) => {
        if (!stored) return;
        // Resolve the authoritative catalog product (the stored state object may
        // be a slim copy). Fall back to its title/handle for the message.
        const product = this.products.find(p =>
          String(p.id) === String(stored.id) ||
          String(p.productId) === String(stored.id) ||
          (stored.handle && p.handle === stored.handle)
        ) || stored;
        const label = product.title || stored.title || stored.handle || 'Producto';
        const vId = this._resolveVariantId(stored);
        const found = product.variants && product.variants.find(v => v.id === vId);
        if (!vId || !found) {
          problems.push(`"${label}" ya no está disponible.`);
          return;
        }
        if (!found.available) {
          problems.push(`"${label}" está agotado.`);
        }
      };

      if (selectedCover && !coverVariantId) {
        problems.push(`"${selectedCover.title}" ya no está disponible.`);
      } else if (selectedCover) {
        const coverFound = selectedCover.variants.find(v => v.id === coverVariantId);
        if (coverFound && !coverFound.available) {
          problems.push(`"${selectedCover.title}" está agotado.`);
        }
      }

      this.state.interiors.forEach(checkProduct);
      this.state.accessories.forEach(checkProduct);

      return problems;
    }

    async handleCheckout(e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();

      const errorContainer = this._createErrorContainer();
      errorContainer.textContent = '';
      errorContainer.classList.add('hidden');

      const btn = this.querySelector('#bundle-sticky-cta');
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = t('adding_to_cart', 'Agregando...') || 'Agregando...';
      }

      try {
        const items = [];
        const selectedCover = this.state.selectedCover;

        const bundleId = selectedCover ? `bundle-${Date.now()}-${Math.floor(Math.random() * 10000)}` : null;
        const coverVariantId = selectedCover ? this._resolveVariantId(selectedCover) : null;

        if (selectedCover && !coverVariantId) {
          throw new Error('No se pudo resolver la variante de la portada seleccionada.');
        }

        // Validate availability of every variant we are about to add BEFORE the
        // network round-trip. A single sold-out/removed variant would otherwise
        // make Shopify reject the whole bundle with only a generic 422.
        const problems = this._validateBundleItems(selectedCover, coverVariantId);
        if (problems.length) {
          throw new Error(problems.join(' '));
        }

        if (coverVariantId) {
          const coverType = this.state.coverType === 'mini'
            ? t('cover_type_mini', 'Mini')
            : t('cover_type_original', 'Original');
          const ring = this.state.ring === 'gold'
            ? t('ring_gold', 'Gold')
            : t('ring_silver', 'Silver');

          items.push({
            id: coverVariantId,
            quantity: 1,
            properties: {
              _bundle_id: bundleId,
              Parent: 'Custom Bundle',
              Size: coverType,
              Binder: selectedCover.title || '',
              Ring: ring,
              Includes: this._getFormattedBundleComponents(),
              ...this._getEngravingProperties()
            }
          });
        }

        const pushChild = (p) => {
          const vId = this._resolveVariantId(p);
          if (!vId) return;
          const item = { id: vId, quantity: p.quantity || 1 };
          // Guarantee every product in the bundle carries the same _bundle_id so
          // the cart can group the cover + each component under one bundle.
          // Merge (rather than overwrite) so any existing properties are kept.
          if (bundleId) {
            item.properties = Object.assign({}, p.properties || {}, { _bundle_id: bundleId });
          }
          items.push(item);
        };
        this.state.interiors.forEach(pushChild);
        this.state.accessories.forEach(pushChild);

        if (items.length === 0) {
          throw new Error('Agrega productos a tu bundle primero.');
        }

        // Collect the section ids the theme wants re-rendered (e.g. the cart
        // drawer) so we can ask Shopify to return their freshly rendered HTML.
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const addBody = { items };
        if (sectionsToBundle.length) {
          addBody.sections = sectionsToBundle.join(',');
          addBody.sections_url = window.location.pathname;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(addBody)
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          console.error('Add to cart failed:', response.status, error);
          throw new Error(error.description || 'Error al agregar al carrito. Intenta de nuevo.');
        }

        const addResult = await response.json();

        // /cart/add.js only returns the added line item(s), not the full cart
        // (no item_count) and, with the `sections` param, the rendered section
        // HTML in `sections`. The theme's cart:change handler expects
        // `event.detail.cart.sections[sectionId]`, so build a complete cart
        // object the same way the theme's ProductForm does.
        const fullCart = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart.js` : '/cart.js', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }).then(r => r.json());

        if (addResult.sections) {
          fullCart.sections = addResult.sections;
        }

        const result = fullCart;

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: result,
            sourceId: 'bundle-configurator',
            data: { source: 'bundle-configurator', itemCount: result.item_count, sections: result.sections || {} }
          }
        }));
        document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart: result } }));

        // Re-enable the sticky CTA: it was disabled at the start of this method
        // and the error path was the only place that re-enabled it. Without this,
        // a successful add leaves the "next" button disabled (ignoring clicks)
        // until the post-checkout modal calls updateUI().
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }

        this.openPostCheckoutModal();
      } catch (err) {
        console.error('[BundleConfigurator] Checkout failed:', err);
        this._showBundleError(err.message || 'Error al agregar al carrito. Intenta de nuevo.');
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          this.updateStickyCTA();
        }
      }
    }

    openPostCheckoutModal() {
      const modal = document.querySelector('#post-checkout-modal');
      if (!modal) return;

      const titleEl = document.querySelector('#post-checkout-title');
      const freshTextEl = document.querySelector('#post-checkout-fresh-text');
      const sameTextEl = document.querySelector('#post-checkout-same-text');
      const checkoutTextEl = document.querySelector('#post-checkout-checkout-text');
      const exploreTextEl = document.querySelector('#post-checkout-explore-text');

      if (titleEl) titleEl.textContent = t('post_checkout_title', 'What would you like to do next?');
      if (freshTextEl) freshTextEl.textContent = t('post_checkout_fresh', 'Want to create a new Ofelia with fresh looks');
      if (sameTextEl) sameTextEl.textContent = t('post_checkout_same', 'Want to use these settings to create a new Ofelia');
      if (checkoutTextEl) checkoutTextEl.textContent = t('post_checkout_checkout', 'Ready to buy - go to checkout');
      if (exploreTextEl) exploreTextEl.textContent = t('post_checkout_explore', 'Want to explore more products');

      // Clear any pointer-events lock left behind by a previous forced close.
      modal.style.pointerEvents = '';

      if (typeof modal.show === 'function') {
        modal.show();
      } else {
        modal.setAttribute('open', '');
      }
    }

    closePostCheckoutModal() {
      const modal = document.querySelector('#post-checkout-modal');
      if (!modal) return;

      // The theme's modal fades out via opacity and only applies `display: none`
      // once the leave animation's `.finished` promise resolves. If that promise
      // never resolves (e.g. the modal gets re-parented while the reset below
      // runs), the element stays `display: block` with `opacity: 0` — an
      // invisible, full-screen overlay (its ::part(overlay) is
      // `position: fixed; inset: 0`) that silently swallows every click and
      // makes the rest of the page (especially the sticky "next" button)
      // unclickable. Disable pointer events immediately so it can never block
      // interaction, regardless of the animation state.
      modal.style.pointerEvents = 'none';

      if (typeof modal.hide === 'function') {
        modal.hide().catch(() => {});
      }
      modal.removeAttribute('open');

      // Safety net: if the theme's hide animation did not complete (and thus
      // never set `display: none` / moved the modal back), force it closed so
      // no invisible layer is left behind.
      setTimeout(() => {
        if (!modal.isConnected) return;
        if (modal.hasAttribute('open')) modal.removeAttribute('open');
        modal.style.setProperty('display', 'none');
      }, 500);
    }

    _resetBundleState() {
      this.setState({
        currentStep: 1,
        coverType: 'original',
        selectedColor: null,
        selectedCover: null,
        ring: 'silver',
        engraving: '',
        engravingColor: '#D4AF37',
        engravingFont: 'Sans Serif',
        engravingFontFamily: "'DM Sans', sans-serif",
        engravingFontWeight: 500,
        interiors: [],
        accessories: [],
        quantities: {},
        interiorPriceMin: null,
        interiorPriceMax: null,
        interiorInStockOnly: false,
        interiorSelectedTags: [],
        accessoryPriceMin: null,
        accessoryPriceMax: null,
        accessoryInStockOnly: false,
        accessorySelectedTags: []
      });
    }
  }

  if (!window.customElements.get('bundle-configurator')) {
    window.customElements.define('bundle-configurator', BundleConfigurator);
  }
})();